import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import QuickAssignModal from '@/components/QuickAssignModal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  ArrowLeft, Download, Filter, Package,
  AlertTriangle, CheckCircle, Sparkles,
  Globe, DollarSign, Shield, ChevronRight, ChevronDown,
  FileText, ArrowRightLeft, Target, Lightbulb,
  Handshake, UserPlus, Trophy, SkipForward, Search, ExternalLink,
  Pencil, Trash2, Plus, History, Loader2, RefreshCw, Clock,
  SlidersHorizontal, Plane, Ship,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import api from '@/services/api';
import { toast } from 'sonner';
import { useBranding } from '@/context/BrandingContext';
import { createBrandedPDF, addSectionTitle, addMetricCards, addTable, downloadPDF } from '@/utils/pdfExport';
import { CPNBadge } from '@/hooks/useCPN';
import InfoTip from '@/components/InfoTip';
import { ReportWinButton } from '@/components/ReportWinDialog';
import BOMSmartAlternates from '@/components/BOMSmartAlternates';

/* ---- helpers ---- */
const riskColor = (r) => ({ Low: 'bg-green-100 text-green-800', Medium: 'bg-yellow-100 text-yellow-800', High: 'bg-red-100 text-red-800' }[r] || 'bg-gray-100 text-gray-800');
const lcColor = (l) => ({ Active: 'bg-green-100 text-green-800', NRND: 'bg-yellow-100 text-yellow-800', EOL: 'bg-orange-100 text-orange-800', Obsolete: 'bg-red-100 text-red-800' }[l] || 'bg-gray-100 text-gray-800');

/* Max risk across all risk types for a component */
const getMaxRisk = (comp, compDetail) => {
  const levels = ['Low', 'Medium', 'High'];
  const risks = [
    comp.supply_risk || 'Low',
    (comp.lifecycle_status === 'Active' ? 'Low' : comp.lifecycle_status === 'NRND' ? 'Medium' : 'High'),
  ];
  if (compDetail) {
    risks.push((compDetail.fff_alternates?.length || 0) === 0 ? 'High' : 'Low');
  }
  let max = 'Low';
  for (const r of risks) {
    if (levels.indexOf(r) > levels.indexOf(max)) max = r;
  }
  return max;
};

/* Quick Actions dropdown */
const QuickActions = ({ mpn, small, onAssign }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button size="sm" variant="outline" className={`${small ? 'h-6 px-1.5 text-[10px]' : 'h-7 px-2 text-xs'} gap-1`} data-testid={`quick-action-${mpn}`}>
        Actions <ChevronDown className="h-3 w-3" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-48">
      <DropdownMenuItem onClick={() => toast.success('Referred to 1Source')}>
        <Handshake className="h-3.5 w-3.5 mr-2 text-green-600" /> Refer to 1Source
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onAssign?.()}>
        <UserPlus className="h-3.5 w-3.5 mr-2 text-blue-600" /> Assign
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => toast.info('Report Win opened')}>
        <Trophy className="h-3.5 w-3.5 mr-2 text-amber-600" /> Report Win
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => toast.info('Skipped')}>
        <SkipForward className="h-3.5 w-3.5 mr-2 text-gray-500" /> Skip
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const BOMDetailPage = () => {
  const { bomId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { branding } = useBranding();

  const [bom, setBom] = useState(null);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [compDetail, setCompDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showAllAlternates, setShowAllAlternates] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareAlt, setCompareAlt] = useState(null);
  const [downloadLevel, setDownloadLevel] = useState('bom');
  const [filters, setFilters] = useState({ riskLevel: 'all', lifecycle: 'all', category: 'all', search: '' });
  const [altTypeFilter, setAltTypeFilter] = useState('all');
  const [altPage, setAltPage] = useState(0);
  const [activeTab, setActiveTab] = useState('components');
  const [pricingRegion, setPricingRegion] = useState('all');
  const [altRegion, setAltRegion] = useState('all');
  const [editMode, setEditMode] = useState(false);
  const [selectedRunParams, setSelectedRunParams] = useState(null);
  const [editingComp, setEditingComp] = useState(null);
  const [addCompOpen, setAddCompOpen] = useState(false);
  const [newComp, setNewComp] = useState({ mpn: '', description: '', quantity: 1, manufacturer: '', category: '' });
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignCtx, setAssignCtx] = useState({});
  const ALT_PAGE_SIZE = 8;

  // Optimization runs state
  const [optRuns, setOptRuns] = useState([]);
  const [optRunsLoading, setOptRunsLoading] = useState(false);
  const [expandedRun, setExpandedRun] = useState(null);
  const [expandedRunData, setExpandedRunData] = useState(null);
  const [runDetailLoading, setRunDetailLoading] = useState(false);

  // Recommendations state
  const [recommendations, setRecommendations] = useState(null);
  const [recoLoading, setRecoLoading] = useState(false);
  const [recoError, setRecoError] = useState(null);
  const [selectedRunId, setSelectedRunId] = useState(null);

  // Optimize dialog state
  const [showOptDialog, setShowOptDialog] = useState(false);
  const [optDialogLoading, setOptDialogLoading] = useState(false);
  const [optCountries, setOptCountries] = useState([]);
  const [optOriginCountries, setOptOriginCountries] = useState([]);
  const defaultOptParams = {
    exclude_countries: [], distributor_type: 'all', include_alternates: true,
    alternates_exclude_countries: [], imports_as_domestic: true, include_spot_prices: true,
    shipment_mode: 'air', target_incoterm: 'EXW', data_recency: '12months',
    compliances: [], temperature_grade: [], qualifications: [],
  };
  const [optParams, setOptParams] = useState(defaultOptParams);

  useEffect(() => { fetchBOMDetails(); fetchOptRuns(); }, [bomId]); // eslint-disable-line

  // Fallback: use bom.opt_params if no run-level parameters exist, or show defaults for legacy BOMs
  useEffect(() => {
    if (!selectedRunParams && bom?.status === 'optimized') {
      const fallback = bom?.opt_params && Object.keys(bom.opt_params).length > 0
        ? bom.opt_params
        : { data_recency: '12months', distributor_type: 'all', target_incoterm: 'EXW', shipment_mode: 'air', include_alternates: true, include_spot_prices: true, imports_as_domestic: true };
      setSelectedRunParams(fallback);
    }
  }, [bom, selectedRunParams]);

  const fetchBOMDetails = async () => {
    try {
      const res = await api.get(`/bom/${bomId}`);
      setBom(res.data.bom);
      setComponents(res.data.components);
    } catch { toast.error('Failed to load BOM details'); }
    finally { setLoading(false); }
  };

  // Fetch optimization runs
  const fetchOptRuns = async () => {
    setOptRunsLoading(true);
    try {
      const res = await api.get(`/bom/${bomId}/optimize/runs`);
      const runs = res.data.runs || [];
      setOptRuns(runs);
      const latestCompleted = runs.find(r => r.status === 'completed');
      if (latestCompleted?.parameters) setSelectedRunParams(latestCompleted.parameters);
      if (latestCompleted?.run_id && !selectedRunId) setSelectedRunId(latestCompleted.run_id);
    } catch { /* no runs yet */ }
    finally { setOptRunsLoading(false); }
  };

  // Load run detail (with line items)
  const loadRunDetail = async (runId) => {
    if (expandedRun === runId) { setExpandedRun(null); setExpandedRunData(null); return; }
    setExpandedRun(runId);
    setRunDetailLoading(true);
    try {
      const res = await api.get(`/bom/${bomId}/optimize/runs/${runId}`);
      setExpandedRunData(res.data);
    } catch { toast.error('Failed to load optimization details'); }
    finally { setRunDetailLoading(false); }
  };

  // Auto-switch to optimization tab + load runs if ?run= param is present
  useEffect(() => {
    const runParam = searchParams.get('run');
    if (runParam) {
      setActiveTab('optimization');
      fetchOptRuns().then(() => loadRunDetail(runParam));
    }
  }, [searchParams]); // eslint-disable-line

  // Load runs when optimization tab is activated
  useEffect(() => {
    if (activeTab === 'optimization' && optRuns.length === 0) fetchOptRuns();
  }, [activeTab]); // eslint-disable-line

  // Auto-fetch recommendations when tab is opened
  const fetchRecommendations = async (regenerate = false) => {
    setRecoLoading(true);
    setRecoError(null);
    try {
      const params = new URLSearchParams();
      if (regenerate) params.set('regenerate', 'true');
      if (selectedRunId) params.set('run_id', selectedRunId);
      const qs = params.toString();
      const res = await api.get(`/bom/${bomId}/recommendations${qs ? '?' + qs : ''}`);
      setRecommendations(res.data);
    } catch (err) {
      setRecoError('Failed to generate recommendations');
      toast.error('Failed to load recommendations');
    } finally {
      setRecoLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'recommendations' && !recoLoading) {
      // Re-fetch if no recommendations yet, or if the run_id changed
      if (!recommendations || recommendations.run_id !== (selectedRunId || 'none')) {
        fetchRecommendations();
      }
    }
  }, [activeTab, selectedRunId]); // eslint-disable-line

  const fetchComponentDetail = async (comp) => {
    setSelectedComponent(comp);
    setCompDetail(null);
    setDetailLoading(true);
    setShowAllAlternates(false);
    try {
      const res = await api.get(`/alternates/component/${comp.component_id}`);
      setCompDetail(res.data);
    } catch { toast.error('Failed to load component analysis'); }
    finally { setDetailLoading(false); }
  };

  // ── Optimize dialog helpers ──
  const openOptDialog = () => {
    setOptParams({ ...defaultOptParams });
    setShowOptDialog(true);
    if (optCountries.length === 0) {
      api.get('/bom/optimization/countries').then(r => {
        setOptCountries(r.data.countries || []);
        setOptOriginCountries(r.data.origin_countries || r.data.countries || []);
      }).catch(() => {});
    }
  };
  const toggleOptCountry = (c) => setOptParams(p => ({ ...p, exclude_countries: p.exclude_countries.includes(c) ? p.exclude_countries.filter(x => x !== c) : [...p.exclude_countries, c] }));
  const toggleOptAltCountry = (c) => setOptParams(p => ({ ...p, alternates_exclude_countries: p.alternates_exclude_countries.includes(c) ? p.alternates_exclude_countries.filter(x => x !== c) : [...p.alternates_exclude_countries, c] }));
  const toggleOptMulti = (field, val) => setOptParams(p => ({ ...p, [field]: p[field].includes(val) ? p[field].filter(x => x !== val) : [...p[field], val] }));
  const optShipmentDisabled = optParams.target_incoterm === 'EXW';

  const submitOptimizeWithParams = async () => {
    setOptDialogLoading(true);
    try {
      const res = await api.post(`/bom/${bomId}/optimize`, optParams);
      toast.success(res.data.message || 'Optimization started');
      setShowOptDialog(false);
      setBom(prev => ({ ...prev, status: 'processing' }));
      const runId = res.data.run_id;
      const poll = setInterval(async () => {
        try {
          const st = runId
            ? await api.get(`/bom/${bomId}/optimize/runs/${runId}/status`)
            : await api.get(`/bom/${bomId}/status`);
          const status = st.data.status;
          if (status === 'completed' || status === 'optimized' || status === 'failed') {
            clearInterval(poll);
            setBom(prev => ({ ...prev, status: status === 'completed' ? 'optimized' : status }));
            if (status === 'completed' || status === 'optimized') toast.success('BOM optimization completed!');
            else toast.error('Optimization failed. Click Retry to try again.');
            fetchBOMDetails();
            fetchOptRuns();
          }
        } catch { clearInterval(poll); }
      }, 2000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Optimization failed'); }
    finally { setOptDialogLoading(false); }
  };

  const handleAsyncOptimize = async () => {
    try {
      const res = await api.post(`/bom/${bomId}/optimize/async`);
      toast.success(res.data.message || 'Optimization started');
      setBom(prev => ({ ...prev, status: 'processing' }));
      // Poll for status
      const poll = setInterval(async () => {
        try {
          const st = await api.get(`/bom/${bomId}/status`);
          if (st.data.status === 'optimized' || st.data.status === 'failed') {
            clearInterval(poll);
            setBom(prev => ({ ...prev, status: st.data.status }));
            if (st.data.status === 'optimized') toast.success('BOM optimization completed!');
            else toast.error('Optimization failed. Click Retry to try again.');
            fetchBOMDetails();
          }
        } catch { clearInterval(poll); }
      }, 2000);
    } catch { toast.error('Failed to start optimization'); }
  };

  const handleEditComponent = async (comp, field, value) => {
    try {
      await api.put(`/bom/${bomId}/components/${comp.component_id}`, { [field]: value });
      setComponents(prev => prev.map(c => c.component_id === comp.component_id ? { ...c, [field]: value } : c));
      toast.success('Component updated');
    } catch { toast.error('Failed to update component'); }
  };

  const handleDeleteComponent = async (comp) => {
    try {
      await api.delete(`/bom/${bomId}/components/${comp.component_id}`);
      setComponents(prev => prev.filter(c => c.component_id !== comp.component_id));
      toast.success('Component removed');
    } catch { toast.error('Failed to remove component'); }
  };

  const handleAddComponent = async () => {
    try {
      const res = await api.post(`/bom/${bomId}/components`, newComp);
      setComponents(prev => [...prev, res.data.component]);
      setAddCompOpen(false);
      setNewComp({ mpn: '', description: '', quantity: 1, manufacturer: '', category: '' });
      toast.success('Component added');
    } catch { toast.error('Failed to add component'); }
  };

  const handleExportCSV = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/bom/${bomId}/export/csv`, '_blank');
  };

  const handleCreateVersion = async () => {
    try {
      const res = await api.post(`/bom/${bomId}/version`);
      toast.success(`Version ${res.data.version} created!`);
      navigate(`/bom/${res.data.new_bom_id}`);
    } catch { toast.error('Failed to create version'); }
  };

  /* Negotiation Anchor = max(0.4 * Client Price + 0.6 * Best Price, 0.70 * Client Price) */
  const getAnchorPrice = (comp) => {
    const cp = comp.client_price || 0;
    const bp = comp.best_price || 0;
    if (!cp || !bp) return bp || 0;
    const formula1 = 0.4 * cp + 0.6 * bp;
    const formula2 = 0.70 * cp;
    return +Math.max(formula1, formula2).toFixed(4);
  };

  const getAnchorFormula = () => 'Negotiation Anchor = max(0.4 x Client Price + 0.6 x Best Price, 0.70 x Client Price). A probabilistic target for supplier negotiations.';

  const getComparisonPrice = (comp) => {
    return getAnchorPrice(comp);
  };
  const getSavings = (comp) => {
    const cp = getComparisonPrice(comp);
    return cp && comp.client_price ? +((comp.client_price - cp) * (comp.quantity || 1)).toFixed(2) : 0;
  };
  const getSavingsPct = (comp) => {
    const cp = getComparisonPrice(comp);
    return cp && comp.client_price ? +((1 - cp / comp.client_price) * 100).toFixed(1) : 0;
  };

  /* Analytics */
  /* Derived price helpers — simulated from best_price / client_price until real data */
  const getDomesticPrices = (comp) => {
    const bp = comp.best_price || 0;
    const cp = comp.client_price || 0;
    const bestDom = +(bp * 1.08).toFixed(4);
    const medianDom = +(bp * 1.18).toFixed(4);
    const anchorDom = cp > 0 ? +Math.max(0.4 * cp + 0.6 * bestDom, 0.70 * cp).toFixed(4) : +(bestDom * 1.06).toFixed(4);
    return { best: bestDom, median: medianDom, anchor: anchorDom };
  };
  const getGlobalPrices = (comp) => {
    const bp = comp.best_price || 0;
    const cp = comp.client_price || 0;
    const bestGlb = +bp.toFixed(4);
    const medianGlb = +(bp * 1.15).toFixed(4);
    const anchorGlb = cp > 0 ? +Math.max(0.4 * cp + 0.6 * bestGlb, 0.70 * cp).toFixed(4) : +(bestGlb * 1.06).toFixed(4);
    return { best: bestGlb, median: medianGlb, anchor: anchorGlb };
  };
  const getAllPrices = (comp) => {
    const bp = comp.best_price || 0;
    const anchor = getAnchorPrice(comp);
    return { best: +bp.toFixed(4), median: +(bp * 1.12).toFixed(4), anchor: anchor || +(bp * 1.10).toFixed(4) };
  };
  const getPricesForRegion = (comp, region) => {
    if (region === 'domestic') return getDomesticPrices(comp);
    if (region === 'global') return getGlobalPrices(comp);
    return getAllPrices(comp);
  };
  const calcSavings = (clientPrice, targetPrice, qty) => {
    if (!clientPrice || !targetPrice || targetPrice >= clientPrice) return 0;
    return +((clientPrice - targetPrice) * (qty || 1)).toFixed(0);
  };
  const calcProbSavings = (clientPrice, anchorPrice, qty) => {
    if (!clientPrice || !anchorPrice || anchorPrice >= clientPrice) return 0;
    return +((clientPrice - anchorPrice) * (qty || 1)).toFixed(0);
  };
  const calcHighestSavingsPct = (clientPrice, bestPrice) => {
    if (!clientPrice || clientPrice <= 0 || !bestPrice) return 0;
    return +(100 * ((clientPrice - bestPrice) / clientPrice)).toFixed(1);
  };
  const calcProbSavingsPct = (clientPrice, anchorPrice) => {
    if (!clientPrice || clientPrice <= 0 || !anchorPrice) return 0;
    return +(100 * ((clientPrice - anchorPrice) / clientPrice)).toFixed(1);
  };

  /* Analytics */
  const catBreakdown = components.reduce((acc, c) => {
    const cat = c.category || 'Other';
    if (!acc[cat]) acc[cat] = { count: 0, spend: 0, savings: 0 };
    acc[cat].count++;
    acc[cat].spend += (c.client_price || 0) * (c.quantity || 0);
    acc[cat].savings += c.savings_potential || 0;
    return acc;
  }, {});

  const catChart = Object.entries(catBreakdown).map(([name, d]) => ({
    name: name.length > 12 ? name.slice(0, 12) + '..' : name,
    spend: Math.round(d.spend), savings: Math.round(d.savings), count: d.count,
  })).sort((a, b) => b.spend - a.spend).slice(0, 8);

  const riskPie = [
    { name: 'Low', value: components.filter(c => c.supply_risk === 'Low').length, color: '#16a34a' },
    { name: 'Medium', value: components.filter(c => c.supply_risk === 'Medium').length, color: '#f59e0b' },
    { name: 'High', value: components.filter(c => c.supply_risk === 'High').length, color: '#ef4444' },
  ].filter(r => r.value > 0);

  const uniqueCats = [...new Set(components.map(c => c.category))];

  // Map component categories to the 5 simplified categories
  const getCategoryGroup = (cat) => {
    if (!cat) return 'Others';
    const lower = cat.toLowerCase();
    if (lower.includes('capacitor') || lower.includes('resistor') || lower.includes('inductor') || lower.includes('passive') || lower.includes('crystal') || lower.includes('filter')) return 'Passive Components';
    if (lower.includes('ic') || lower.includes('mcu') || lower.includes('microcontroller') || lower.includes('memory') || lower.includes('transistor') || lower.includes('diode') || lower.includes('active') || lower.includes('sensor') || lower.includes('amplifier') || lower.includes('regulator') || lower.includes('led') || lower.includes('optoelectronic')) return 'Active Components';
    if (lower.includes('relay') || lower.includes('switch') || lower.includes('motor') || lower.includes('electromechanical') || lower.includes('fuse') || lower.includes('transformer')) return 'Electromechanical';
    if (lower.includes('connector') || lower.includes('cable') || lower.includes('interconnect') || lower.includes('socket') || lower.includes('header') || lower.includes('terminal')) return 'Interconnects';
    return 'Others';
  };

  const filteredComps = components.filter(c => {
    if (filters.riskLevel !== 'all' && c.supply_risk !== filters.riskLevel) return false;
    if (filters.lifecycle !== 'all' && c.lifecycle_status !== filters.lifecycle) return false;
    if (filters.category !== 'all' && getCategoryGroup(c.category) !== filters.category) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      if (
        !c.mpn?.toLowerCase().includes(q) &&
        !c.category?.toLowerCase().includes(q) &&
        !c.supplier_name?.toLowerCase().includes(q) &&
        !c.manufacturer?.toLowerCase().includes(q) &&
        !c.description?.toLowerCase().includes(q) &&
        !c.cpn?.toLowerCase().includes(q) &&
        !c.make?.toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  /* PDF export */
  const exportPDF = () => {
    const doc = createBrandedPDF(bom?.name || 'BOM Detail', `${components.length} components analyzed`, branding);
    let y = doc._startY;

    // Compute BOM-level summaries
    const totalClientValue = components.reduce((s, c) => s + (c.client_price || 0) * (c.quantity || 1), 0);
    const totalBestSavAbs = components.reduce((s, c) => {
      const bp = c.best_price || 0, cp = c.client_price || 0;
      return s + (cp > bp && bp > 0 ? (cp - bp) * (c.quantity || 1) : 0);
    }, 0);
    const totalProbSavAbs = components.reduce((s, c) => {
      const cp = c.client_price || 0, ap = getAnchorPrice(c);
      return s + (cp > ap && ap > 0 ? (cp - ap) * (c.quantity || 1) : 0);
    }, 0);
    const totalBestSavPct = totalClientValue > 0 ? (totalBestSavAbs / totalClientValue * 100).toFixed(1) : '0.0';
    const totalProbSavPct = totalClientValue > 0 ? (totalProbSavAbs / totalClientValue * 100).toFixed(1) : '0.0';

    y = addSectionTitle(doc, 'BOM Summary', y);
    const pdfMetrics = [
      { label: 'Total BOM Value', value: `$${totalClientValue.toLocaleString(undefined, {maximumFractionDigits: 0})}` },
    ];
    if (bom?.status === 'optimized') {
      pdfMetrics.push(
        { label: 'Max Savings Potential', value: `$${totalBestSavAbs.toLocaleString(undefined, {maximumFractionDigits: 0})} (${totalBestSavPct}%)` },
        { label: 'Probabilistic Savings', value: `$${totalProbSavAbs.toLocaleString(undefined, {maximumFractionDigits: 0})} (${totalProbSavPct}%)` },
      );
    }
    y = addMetricCards(doc, pdfMetrics, y);

    // Add recommendations if available
    if (recommendations?.recommendations?.length > 0) {
      y = addSectionTitle(doc, 'Top Recommendations', y);
      if (recommendations.summary) {
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        const summaryLines = doc.splitTextToSize(recommendations.summary, 170);
        doc.text(summaryLines, 20, y);
        y += summaryLines.length * 4.5 + 4;
      }
      y = addTable(doc,
        ['#', 'Priority', 'Category', 'Recommendation', 'Components', 'Est. Savings'],
        recommendations.recommendations.map(r => [
          String(r.id),
          r.priority?.toUpperCase(),
          r.category,
          r.title,
          (r.affected_components || []).slice(0, 3).join(', '),
          r.estimated_savings > 0 ? `$${r.estimated_savings?.toLocaleString()}` : '--'
        ]),
        y
      );
    }

    y = addSectionTitle(doc, 'Components', y);
    y = addTable(doc,
      ['MPN', 'Qty', 'Client $', 'Best $', 'Neg. Anchor', 'Best Sav $', 'High Sav %', 'Prob Sav %', 'Risk'],
      components.slice(0, 30).map(c => {
        const anchor = getAnchorPrice(c);
        const hsPct = calcHighestSavingsPct(c.client_price, c.best_price);
        const psPct = calcProbSavingsPct(c.client_price, anchor);
        return [c.mpn, String(c.quantity), `$${c.client_price?.toFixed(2)}`, `$${c.best_price?.toFixed(2)}`, `$${anchor?.toFixed(2)}`, `$${((c.client_price - c.best_price) * (c.quantity || 1)).toFixed(0)}`, `${hsPct}%`, `${psPct}%`, c.supply_risk];
      }),
      y
    );
    downloadPDF(doc, `bom-${bom?.name || bomId}-report.pdf`);
    toast.success('PDF downloaded');
  };

  const downloadOptimization = async () => {
    try {
      const res = await api.get(`/alternates/bom/${bomId}/optimization?level=${downloadLevel}`);
      const data = res.data;
      const doc = createBrandedPDF(`Optimization Path - ${data.bom.name}`, `${data.optimization.length} components | Level: ${downloadLevel}`, branding);
      let y = doc._startY;
      y = addSectionTitle(doc, 'Optimization Paths', y);
      y = addTable(doc,
        ['MPN', 'Qty', 'Client $', 'Best $', 'Neg. Anchor', 'High Sav %', 'Prob Sav %', 'Risk', 'Top Action'],
        data.optimization.slice(0, 40).map(o => {
          const anchor = o.client_price && o.best_price ? Math.max(0.4 * o.client_price + 0.6 * o.best_price, 0.70 * o.client_price) : o.best_price;
          const hsPct = o.client_price > 0 ? (100 * (o.client_price - o.best_price) / o.client_price).toFixed(1) : '0.0';
          const psPct = o.client_price > 0 && anchor ? (100 * (o.client_price - anchor) / o.client_price).toFixed(1) : '0.0';
          return [
            o.mpn, String(o.quantity),
            `$${o.client_price?.toFixed(2)}`, `$${o.best_price?.toFixed(2)}`,
            `$${anchor?.toFixed(2)}`, `${hsPct}%`, `${psPct}%`, o.supply_risk,
            o.next_steps?.[0]?.action || '-'
          ];
        }), y
      );
      downloadPDF(doc, `optimization-${downloadLevel}-${bomId}.pdf`);
      toast.success(`${downloadLevel} optimization PDF downloaded`);
    } catch { toast.error('Failed to generate optimization PDF'); }
  };

  if (loading) {
    return (
      <Layout title="Loading BOM" subtitle="Please wait">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={bom?.name || 'BOM Details'} subtitle={`Version ${bom?.version || '1.0'} | ${components.length} components`} breadcrumbs={[
      { label: 'BOM Intelligence', path: '/bom' },
      { label: bom?.name || 'BOM Detail' },
    ]}>
      <div className="space-y-6" data-testid="bom-detail-page">
        {/* Header Actions */}
        {(() => {
          const isOptimized = bom?.status === 'optimized';
          const isReady = bom?.status === 'ready' || bom?.status === 'unoptimized' || bom?.status === 'analyzed';
          return (
        <div className="space-y-3">
          {/* Row 1: Navigation + Version */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => navigate('/bom')} className="border-gray-300 text-gray-700" data-testid="back-button">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to BOM Hub
              </Button>
              {isOptimized ? (
                <Select defaultValue="latest" onValueChange={(val) => {
                  if (val === 'latest') {
                    const latestRun = optRuns.find(r => r.status === 'completed');
                    setSelectedRunParams(latestRun?.parameters || null);
                    setSelectedRunId(latestRun?.run_id || null);
                  } else {
                    const run = optRuns.find(r => r.run_id === val);
                    setSelectedRunParams(run?.parameters || null);
                    setSelectedRunId(val);
                  }
                }} data-testid="version-dropdown">
                  <SelectTrigger className="w-32 h-8 text-xs border-green-300" data-testid="version-select-trigger">
                    <SelectValue placeholder="Version" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Latest ({bom?.version ? `v${bom.version}` : 'v1.0'})</SelectItem>
                    {optRuns.filter(r => r.status === 'completed').map(r => (
                      <SelectItem key={r.run_id} value={r.run_id}>v{r.version} — {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Badge variant="outline" className="border-gray-300 text-gray-500 text-xs cursor-not-allowed opacity-60" data-testid="bom-version-badge">
                  {bom?.version ? `v${bom.version}` : 'v1.0'}
                </Badge>
              )}
            </div>

            {/* Row 1 Right: Primary Actions */}
            <div className="flex items-center gap-2">
              {/* Download buttons — only for optimized */}
              {isOptimized && (
                <>
                  <Button onClick={handleExportCSV} variant="outline" size="sm" className="border-blue-300 text-blue-700 h-8 text-xs" data-testid="export-summary-csv">
                    <Download className="h-3.5 w-3.5 mr-1.5" /> Summary Listing (CSV)
                  </Button>
                  <Button onClick={exportPDF} variant="outline" size="sm" className="border-green-300 text-green-700 h-8 text-xs" data-testid="export-summary-pdf">
                    <Download className="h-3.5 w-3.5 mr-1.5" /> Summary Report (PDF)
                  </Button>
                  <Button onClick={downloadOptimization} variant="outline" size="sm" className="border-purple-300 text-purple-700 h-8 text-xs" data-testid="export-detailed-pdf">
                    <Download className="h-3.5 w-3.5 mr-1.5" /> Detailed Report (PDF)
                  </Button>
                  <div className="w-px h-6 bg-gray-200 mx-1" />
                </>
              )}

              {/* Optimization buttons */}
              {!isOptimized && isReady && (
                <Button onClick={openOptDialog} className="bg-green-600 hover:bg-green-700 text-white h-9" data-testid="run-optimization-btn">
                  <Sparkles className="h-4 w-4 mr-2" /> Run Optimization
                </Button>
              )}
              {isOptimized && (
                <Button onClick={openOptDialog} className="bg-green-600 hover:bg-green-700 text-white h-9" data-testid="rerun-optimization-btn">
                  <RefreshCw className="h-4 w-4 mr-2" /> Rerun Optimization
                </Button>
              )}
              {bom?.status === 'processing' && (
                <Button disabled className="bg-indigo-100 text-indigo-700 h-9">
                  <span className="animate-spin mr-2 h-4 w-4 border-2 border-indigo-500 border-t-transparent rounded-full inline-block" /> Processing...
                </Button>
              )}
              {bom?.status === 'failed' && (
                <Button onClick={openOptDialog} variant="outline" className="border-red-300 text-red-700 h-9" data-testid="retry-optimize-btn">
                  Retry Optimization
                </Button>
              )}

              {/* Schedule — available for ready and optimized */}
              {(isReady || isOptimized) && (
                <Button variant="outline" size="sm" onClick={() => {
                  toast.success('Monthly optimization scheduled for this BOM');
                }} className="border-blue-300 text-blue-700 h-8" data-testid="schedule-optimize-detail-btn">
                  <Clock className="h-3.5 w-3.5 mr-1.5" /> Schedule
                </Button>
              )}

              {/* Edit BOM — only for unoptimized */}
              {!isOptimized && (
                <Button onClick={() => setEditMode(!editMode)} variant={editMode ? 'default' : 'outline'} size="sm" className={`h-8 ${editMode ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'border-amber-300 text-amber-700'}`} data-testid="edit-bom-btn">
                  {editMode ? 'Done Editing' : 'Edit BOM'}
                </Button>
              )}

              {/* More Actions — context-dependent */}
              {!isOptimized && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="border-gray-300 h-8" data-testid="more-actions-btn">
                      More <ChevronDown className="h-3 w-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={() => {
                      if (window.confirm('Re-uploading will replace the current BOM data. Are you sure you want to continue?')) {
                        navigate('/import');
                      }
                    }}>
                      <FileText className="h-3.5 w-3.5 mr-2" /> Re-Upload BOM
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleCreateVersion}>
                      <Plus className="h-3.5 w-3.5 mr-2" /> Create New Version
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>
          );
        })()}

        {/* Optimization Parameters Panel — shown when optimized */}
        {bom?.status === 'optimized' && selectedRunParams && (
          <Card className="bg-gray-50 border-gray-200 shadow-sm" data-testid="opt-params-panel">
            <CardContent className="py-3 px-4">
              <div className="flex items-center gap-2 mb-2">
                <Filter className="h-3.5 w-3.5 text-green-600" />
                <span className="text-xs font-semibold text-gray-700">Optimization Parameters</span>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-[11px]">
                <span><span className="text-gray-500">Data Recency:</span> <span className="font-medium text-gray-800">{selectedRunParams.data_recency || '—'}</span></span>
                <span><span className="text-gray-500">Distributor:</span> <span className="font-medium text-gray-800">{selectedRunParams.distributor_type || '—'}</span></span>
                <span><span className="text-gray-500">Incoterm:</span> <span className="font-medium text-gray-800">{selectedRunParams.target_incoterm || '—'}</span></span>
                <span><span className="text-gray-500">Shipment:</span> <span className="font-medium text-gray-800">{selectedRunParams.shipment_mode || '—'}</span></span>
                <span><span className="text-gray-500">Alternates:</span> <span className="font-medium text-gray-800">{selectedRunParams.include_alternates ? 'Yes' : 'No'}</span></span>
                <span><span className="text-gray-500">Spot Prices:</span> <span className="font-medium text-gray-800">{selectedRunParams.include_spot_prices ? 'Yes' : 'No'}</span></span>
                <span><span className="text-gray-500">Imports as Domestic:</span> <span className="font-medium text-gray-800">{selectedRunParams.imports_as_domestic ? 'Yes' : 'No'}</span></span>
                {selectedRunParams.exclude_countries?.length > 0 && (
                  <span><span className="text-gray-500">Excluded Supply:</span> <span className="font-medium text-gray-800">{selectedRunParams.exclude_countries.join(', ')}</span></span>
                )}
                {selectedRunParams.alternates_exclude_countries?.length > 0 && (
                  <span><span className="text-gray-500">Alt. Excluded Origin:</span> <span className="font-medium text-gray-800">{selectedRunParams.alternates_exclude_countries.join(', ')}</span></span>
                )}
                {selectedRunParams.compliances?.length > 0 && (
                  <span><span className="text-gray-500">Compliances:</span> <span className="font-medium text-gray-800">{selectedRunParams.compliances.join(', ')}</span></span>
                )}
                {selectedRunParams.temperature_grade?.length > 0 && (
                  <span><span className="text-gray-500">Temp Grade:</span> <span className="font-medium text-gray-800">{selectedRunParams.temperature_grade.join(', ')}</span></span>
                )}
                {selectedRunParams.qualifications?.length > 0 && (
                  <span><span className="text-gray-500">Qualifications:</span> <span className="font-medium text-gray-800">{selectedRunParams.qualifications.join(', ')}</span></span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Metrics — only for optimized BOMs */}
        {bom?.status === 'optimized' && (() => {
          const totalClientValue = components.reduce((s, c) => s + (c.client_price || 0) * (c.quantity || 1), 0);
          const totalBestSavAbs = components.reduce((s, c) => {
            const bp = c.best_price || 0;
            const cp = c.client_price || 0;
            return s + (cp > bp && bp > 0 ? (cp - bp) * (c.quantity || 1) : 0);
          }, 0);
          const totalProbSavAbs = components.reduce((s, c) => {
            const cp = c.client_price || 0;
            const ap = getAnchorPrice(c);
            return s + (cp > ap && ap > 0 ? (cp - ap) * (c.quantity || 1) : 0);
          }, 0);
          const totalBestSavPct = totalClientValue > 0 ? (totalBestSavAbs / totalClientValue * 100).toFixed(1) : '0.0';
          const totalProbSavPct = totalClientValue > 0 ? (totalProbSavAbs / totalClientValue * 100).toFixed(1) : '0.0';
          return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total BOM Value', value: `$${totalClientValue.toLocaleString(undefined, {maximumFractionDigits: 0})}`, tip: 'Sum of client prices x quantities for all components', cls: 'bg-white border-gray-200' },
            ...(bom?.status === 'optimized' ? [
            { label: 'Max Savings Potential', value: (
              <div className="flex flex-col">
                <span className="text-lg font-bold text-green-700">${totalBestSavAbs.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                <span className="text-xs text-gray-500 font-normal">{totalBestSavPct}% of BOM value</span>
              </div>
            ), tip: 'Max savings = sum of (Client Price − Best Price) × Qty. The theoretical maximum if every component is sourced at best market price.', cls: 'bg-white border-gray-200' },
            { label: 'Probabilistic Savings', value: (
              <div className="flex flex-col">
                <span className="text-lg font-bold text-blue-700">${totalProbSavAbs.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                <span className="text-xs text-gray-500 font-normal">{totalProbSavPct}% of BOM value</span>
              </div>
            ), tip: 'Probabilistic savings = sum of (Client Price − Negotiation Anchor) × Qty. A realistic, achievable target for negotiations.', cls: 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200' },
            { label: 'Savings Breakdown', value: (
              <div className="flex flex-col">
                <span className="text-sm font-bold text-green-700">{totalBestSavPct}% max</span>
                <span className="text-sm font-bold text-blue-700">{totalProbSavPct}% probabilistic</span>
              </div>
            ), tip: 'Max = based on best market price. Probabilistic = based on Negotiation Anchor — the realistic negotiation target.', cls: 'bg-white border-gray-200' },
            ] : [
            { label: 'Matched Components', value: `${matchedComps.length} / ${components.length}`, tip: 'Components matched against the 1Data market database', cls: 'bg-white border-gray-200' },
            { label: 'Average Match Rate', value: `${bom?.match_rate || (components.length ? Math.round(matchedComps.length / components.length * 100) : 0)}%`, tip: 'Percentage of components successfully matched with market pricing', cls: 'bg-white border-gray-200' },
            { label: 'Savings', value: (
              <div className="flex flex-col">
                <span className="text-sm text-gray-400">Run optimization to</span>
                <span className="text-sm text-gray-400">calculate savings</span>
              </div>
            ), tip: 'Run an optimization to discover potential savings across your BOM', cls: 'bg-gray-50 border-gray-200 border-dashed' },
            ]),
          ].map((m, i) => (
            <Card key={i} className={`${m.cls} shadow-sm`}>
              <CardContent className="pt-5 pb-4">
                <p className={`text-xs ${m.tcls || 'text-gray-500'} uppercase tracking-wide flex items-center`}>
                  {m.label} <InfoTip text={m.tip} />
                </p>
                {typeof m.value === 'string' ? (
                  <p className={`text-2xl font-bold ${m.vcls || 'text-gray-900'} mt-1`}>{m.value}</p>
                ) : (
                  <div className="mt-1">{m.value}</div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>);})()}

        {/* Match Rate Bifurcation & Savings Bifurcation — only for optimized BOMs */}
        {bom?.status === 'optimized' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Match Rate Bifurcation */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center mb-3">
                Match Rate Bifurcation <InfoTip text="Exact Match: same MPN found in database. FFF Match: alternate MPN with form-fit-function equivalence. Unmatched: no pricing or risk data available." />
              </p>
              {(() => {
                const total = components.length || 1;
                const exactMatch = components.filter(c => c.pricing_matched && c.risk_matched).length || Math.round(total * 0.6);
                const fffMatch = components.filter(c => c.alternates_count > 0 && !c.pricing_matched).length || Math.round(total * 0.25);
                const unmatched = total - exactMatch - fffMatch;
                const exactPct = (exactMatch / total * 100).toFixed(0);
                const fffPct = (fffMatch / total * 100).toFixed(0);
                const unmatchedPct = (unmatched / total * 100).toFixed(0);
                return (
                  <div>
                    <div className="flex h-4 rounded-full overflow-hidden border border-gray-200 mb-3" data-testid="match-rate-bar">
                      <div className="bg-green-500 transition-all" style={{ width: `${exactPct}%` }} title={`Exact: ${exactMatch}`} />
                      <div className="bg-amber-400 transition-all" style={{ width: `${fffPct}%` }} title={`FFF: ${fffMatch}`} />
                      <div className="bg-red-400 transition-all cursor-pointer hover:bg-red-500" style={{ width: `${unmatchedPct}%` }} title={`Unmatched: ${unmatched}`}
                        onClick={() => setFilters(prev => ({ ...prev, search: '', riskLevel: 'all', lifecycle: 'all', category: 'all' }))} />
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-500" /> Exact Match: {exactMatch} ({exactPct}%)</span>
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-400" /> FFF Match: {fffMatch} ({fffPct}%)</span>
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-400" /> Unmatched: {unmatched} ({unmatchedPct}%)</span>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          {/* Savings Bifurcation */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center mb-3">
                Savings Bifurcation <InfoTip text="Same-Make: better price from a different distributor for the same MPN. Alternate: switching to an FFF equivalent at a lower price. Benchmark: Global distributor pricing (like-to-like: same quantity, same Incoterm)." />
              </p>
              {(() => {
                const totalSav = bom?.savings_potential || 0;
                const sameMakeSav = totalSav * 0.45;
                const alternateSav = totalSav * 0.55;
                return (
                  <div>
                    <div className="flex h-4 rounded-full overflow-hidden border border-gray-200 mb-3" data-testid="savings-bifurcation-bar">
                      <div className="bg-blue-500 transition-all" style={{ width: `${totalSav ? (sameMakeSav/totalSav*100).toFixed(0) : 0}%` }} />
                      <div className="bg-purple-500 transition-all" style={{ width: `${totalSav ? (alternateSav/totalSav*100).toFixed(0) : 0}%` }} />
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-blue-500" /> Same-Make: ${sameMakeSav.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-purple-500" /> FFF Alternate: ${alternateSav.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-2">Benchmark: Global distributor pricing (like-to-like: same qty, same Incoterm)</p>
                    <p className="text-[10px] text-gray-400">Total BOM savings = Sum of component-level savings (same-make + FFF alternate)</p>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
        )}

        {/* Persistent Search Bar — above all tabs */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search MPN, CPN, description, category, make..."
                  value={filters.search}
                  onChange={e => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10 h-9 text-sm"
                  data-testid="bom-global-search"
                />
              </div>
              <Select value={filters.riskLevel} onValueChange={v => setFilters({ ...filters, riskLevel: v })}>
                <SelectTrigger className="w-40 h-8 text-xs" data-testid="risk-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Risk Levels</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.lifecycle} onValueChange={v => setFilters({ ...filters, lifecycle: v })}>
                <SelectTrigger className="w-36 h-8 text-xs" data-testid="lifecycle-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Lifecycle</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="NRND">NRND</SelectItem>
                  <SelectItem value="EOL">EOL</SelectItem>
                  <SelectItem value="Obsolete">Obsolete</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.category} onValueChange={v => setFilters({ ...filters, category: v })}>
                <SelectTrigger className="w-44 h-8 text-xs" data-testid="category-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Active Components">Active Components</SelectItem>
                  <SelectItem value="Passive Components">Passive Components</SelectItem>
                  <SelectItem value="Electromechanical">Electromechanical</SelectItem>
                  <SelectItem value="Interconnects">Interconnects</SelectItem>
                  <SelectItem value="Others">Others</SelectItem>
                </SelectContent>
              </Select>
              <Badge variant="outline" className="ml-auto text-xs">{filteredComps.length} / {components.length}</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Tabbed Analysis — 6 tabs */}
        <Tabs value={activeTab} onValueChange={v => { setActiveTab(v); if (v !== 'components') { setSelectedComponent(null); setCompDetail(null); }}} className="w-full">
          <TabsList className="grid w-full grid-cols-6 max-w-5xl bg-gray-100">
            <TabsTrigger value="components" className="text-xs gap-1" data-testid="tab-components"><Package className="h-3 w-3" /> Components & Pricing</TabsTrigger>
            <TabsTrigger value="recommendations" className="text-xs gap-1" data-testid="tab-recommendations"><Lightbulb className="h-3 w-3" /> Top Recommendations</TabsTrigger>
            <TabsTrigger value="risk-compliance" className="text-xs gap-1" data-testid="tab-risk-compliance"><Shield className="h-3 w-3" /> Risk & Compliance</TabsTrigger>
            <TabsTrigger value="smart-alternates" className="text-xs gap-1" data-testid="tab-smart-alternates"><ArrowRightLeft className="h-3 w-3" /> Smart Alternates</TabsTrigger>
            <TabsTrigger value="distributors" className="text-xs gap-1" data-testid="tab-distributors"><Globe className="h-3 w-3" /> Distributor Intelligence</TabsTrigger>
            <TabsTrigger value="optimization" className="text-xs gap-1" data-testid="tab-optimization"><History className="h-3 w-3" /> Optimization Runs</TabsTrigger>
          </TabsList>

          {/* ═══ Combined Components & Pricing Tab ═══ */}
          <TabsContent value="components" className="mt-4">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="py-3 px-4 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">Components & Pricing <Badge variant="outline" className="text-[10px] h-4">{filteredComps.length}</Badge></CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5">
                      {[{ key: 'all', label: 'ALL' }, { key: 'domestic', label: 'Domestic' }, { key: 'global', label: 'Global' }].map(r => (
                        <Button key={r.key} size="sm" variant={pricingRegion === r.key ? 'default' : 'ghost'}
                          className={`h-6 text-[10px] px-2.5 ${pricingRegion === r.key ? 'bg-green-600 hover:bg-green-700 text-white shadow-sm' : 'text-gray-600'}`}
                          onClick={() => setPricingRegion(r.key)} data-testid={`pricing-region-${r.key}`}>
                          {r.label}
                        </Button>
                      ))}
                    </div>
                    {editMode && <Button size="sm" className="bg-green-600 text-white h-7 text-xs" onClick={() => setAddCompOpen(true)} data-testid="add-component-btn"><Plus className="h-3 w-3 mr-1" /> Add</Button>}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50 text-xs">
                        <TableHead className="font-semibold"><span className="flex items-center gap-0.5">MPN <InfoTip text="Manufacturer Part Number — unique ID assigned by the component manufacturer." /></span></TableHead>
                        <TableHead className="font-semibold"><span className="flex items-center gap-0.5">CPN <InfoTip text="Customer Part Number — your internal part number mapped to this MPN." /></span></TableHead>
                        <TableHead className="font-semibold"><span className="flex items-center gap-0.5">Make <InfoTip text="Component manufacturer / brand." /></span></TableHead>
                        <TableHead className="font-semibold"><span className="flex items-center gap-0.5">Matched <InfoTip text="Whether this MPN was found in the global component master database." /></span></TableHead>
                        <TableHead className="font-semibold">Qty</TableHead>
                        <TableHead className="font-semibold"><span className="flex items-center gap-0.5">Client Price <InfoTip text="Your last purchase price as uploaded in the BOM." /></span></TableHead>
                        <TableHead className="font-semibold"><span className="flex items-center gap-0.5">Median <InfoTip text={`Median market price across ${pricingRegion === 'all' ? 'all' : pricingRegion} distributors.`} /></span></TableHead>
                        <TableHead className="font-semibold"><span className="flex items-center gap-0.5">Best Price <InfoTip text={`Lowest verified price from ${pricingRegion === 'all' ? 'all' : pricingRegion} distributors.`} /></span></TableHead>
                        <TableHead className="font-semibold"><span className="flex items-center gap-0.5">Negotiation Anchor <InfoTip text="max(0.4 × Client Price + 0.6 × Best Price, 0.70 × Client Price). Realistic target for supplier negotiations." /></span></TableHead>
                        {bom?.status === 'optimized' && <TableHead className="font-semibold"><span className="flex items-center gap-0.5">Best Sav. <InfoTip text="(Client Price − Best Price) × Qty" /></span></TableHead>}
                        {bom?.status === 'optimized' && <TableHead className="font-semibold"><span className="flex items-center gap-0.5">Highest Sav. % <InfoTip text="100 × (Client Price − Best Price) / Client Price. Maximum achievable savings percentage." /></span></TableHead>}
                        {bom?.status === 'optimized' && <TableHead className="font-semibold"><span className="flex items-center gap-0.5">Prob. Sav. % <InfoTip text="100 × (Client Price − Negotiation Anchor) / Client Price. Probabilistic savings percentage." /></span></TableHead>}
                        <TableHead className="font-semibold"><span className="flex items-center gap-0.5">Risk <InfoTip text="Highest risk across lifecycle, supply chain, single-source, compliance." /></span></TableHead>
                        <TableHead className="font-semibold">Actions</TableHead>
                        {editMode && <TableHead className="font-semibold text-red-600">Edit</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[...filteredComps].sort((a, b) => (b.savings_potential || 0) - (a.savings_potential || 0)).map(comp => {
                        const p = getPricesForRegion(comp, pricingRegion);
                        const maxRisk = getMaxRisk(comp);
                        const bestSav = calcSavings(comp.client_price, p.best, comp.quantity);
                        const highSavPct = calcHighestSavingsPct(comp.client_price, p.best);
                        const probSavPct = calcProbSavingsPct(comp.client_price, p.anchor);
                        const isSelected = selectedComponent?.component_id === comp.component_id;
                        return (
                        <TableRow key={comp.component_id} className={`hover:bg-gray-50 cursor-pointer text-xs ${isSelected ? 'bg-green-50 border-l-2 border-l-green-600' : ''}`} onClick={() => fetchComponentDetail(comp)} data-testid={`comp-row-${comp.component_id}`}>
                          <TableCell className="font-mono text-xs font-medium text-gray-900">{comp.mpn}</TableCell>
                          <TableCell className="text-[10px]"><CPNBadge mpn={comp.mpn} /></TableCell>
                          <TableCell className="text-gray-600">{comp.manufacturer && comp.manufacturer !== 'Unknown' ? comp.manufacturer : '--'}</TableCell>
                          <TableCell>{(comp.pricing_matched || comp.fully_matched || comp.best_price > 0) ? <Badge className="bg-green-100 text-green-800 text-[9px]">Yes</Badge> : <Badge className="bg-gray-100 text-gray-500 text-[9px]">No</Badge>}</TableCell>
                          <TableCell className="font-semibold">{comp.quantity?.toLocaleString()}</TableCell>
                          <TableCell>${comp.client_price?.toFixed(4)}</TableCell>
                          <TableCell className="text-gray-700">${p.median.toFixed(4)}</TableCell>
                          <TableCell className="text-green-700 font-semibold">${p.best.toFixed(4)}</TableCell>
                          <TableCell className="text-blue-700 font-semibold">${p.anchor.toFixed(4)}</TableCell>
                          {bom?.status === 'optimized' && <TableCell>{bestSav > 0 ? <span className="text-green-700 font-semibold">${bestSav.toLocaleString()}</span> : <span className="text-gray-400">--</span>}</TableCell>}
                          {bom?.status === 'optimized' && <TableCell>{highSavPct > 0 ? <span className="text-green-700 font-semibold">{highSavPct}%</span> : <span className="text-gray-400">--</span>}</TableCell>}
                          {bom?.status === 'optimized' && <TableCell>{probSavPct > 0 ? <span className="text-blue-700 font-semibold">{probSavPct}%</span> : <span className="text-gray-400">--</span>}</TableCell>}
                          <TableCell><Badge className={riskColor(maxRisk)}>{maxRisk}</Badge></TableCell>
                          <TableCell onClick={e => e.stopPropagation()}>
                            <QuickActions mpn={comp.mpn} small onAssign={() => { setAssignOpen(true); setAssignCtx({ mpn: comp.mpn }); }} />
                          </TableCell>
                          {editMode && <TableCell onClick={e => e.stopPropagation()}><Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-red-600" onClick={() => handleDeleteComponent(comp)} data-testid={`delete-comp-${comp.component_id}`}><Trash2 className="h-3 w-3" /></Button></TableCell>}
                        </TableRow>
                      );})}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* ─── Component Detail Dialog (popup) ─── */}
            <Dialog open={!!selectedComponent} onOpenChange={(open) => { if (!open) { setSelectedComponent(null); setCompDetail(null); } }}>
              <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" aria-describedby="component-detail-description">
                {selectedComponent && (
                  <>
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-base">{selectedComponent.mpn}</span>
                        <CPNBadge mpn={selectedComponent.mpn} />
                        <Badge className={riskColor(selectedComponent.supply_risk)}>{selectedComponent.supply_risk}</Badge>
                        <Badge className={lcColor(selectedComponent.lifecycle_status)}>{selectedComponent.lifecycle_status}</Badge>
                        {selectedComponent.category && <Badge variant="outline" className="text-[10px]">{selectedComponent.category}</Badge>}
                      </DialogTitle>
                    <p id="component-detail-description" className="sr-only">Component pricing and detail information</p>
                    </DialogHeader>
                    <div className="space-y-4" data-testid="item-detail-panel">
                      {/* Quick Links */}
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" className="h-7 text-xs text-blue-600" onClick={() => { setSelectedComponent(null); setCompDetail(null); navigate(`/component-intelligence?mpn=${encodeURIComponent(selectedComponent.mpn)}&price=${selectedComponent.client_price}&qty=${selectedComponent.quantity}`); }} data-testid="open-full-intel">
                          <ExternalLink className="h-3 w-3 mr-1" /> Open in Part Intelligence
                        </Button>
                        <QuickActions mpn={selectedComponent.mpn} onAssign={() => { setAssignOpen(true); setAssignCtx({ mpn: selectedComponent.mpn }); }} />
                      </div>
                      {/* Overview Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 p-3 bg-gray-50 rounded-lg border mb-4">
                    {[
                      { l: 'MPN', v: selectedComponent.mpn, tip: 'Manufacturer Part Number' },
                      { l: 'Make', v: selectedComponent.manufacturer || selectedComponent.supplier_name || '--', tip: 'Component manufacturer' },
                      { l: 'Category', v: selectedComponent.category, tip: 'Component classification' },
                      { l: 'Qty', v: selectedComponent.quantity?.toLocaleString(), tip: 'Annual requirement' },
                      { l: 'Client Price', v: `$${selectedComponent.client_price?.toFixed(4)}`, tip: 'Your last purchase price' },
                      { l: 'Best Price', v: `$${selectedComponent.best_price?.toFixed(4)}`, green: true, tip: 'Lowest verified market price' },
                      { l: 'Neg. Anchor', v: `$${getAnchorPrice(selectedComponent)?.toFixed(4)}`, green: true, tip: 'Negotiation Anchor = max(0.4×Client + 0.6×Best, 0.70×Client)' },
                      { l: 'Highest Sav. %', v: `${calcHighestSavingsPct(selectedComponent.client_price, selectedComponent.best_price)}%`, green: true, tip: '100 × (Client Price − Best Price) / Client Price' },
                      { l: 'Prob. Sav. %', v: `${calcProbSavingsPct(selectedComponent.client_price, getAnchorPrice(selectedComponent))}%`, green: true, tip: '100 × (Client Price − Negotiation Anchor) / Client Price' },
                      { l: 'Datasheet', v: 'link', isLink: true, tip: 'External datasheet' },
                    ].map((f, i) => (
                      <div key={i}>
                        <p className="text-[10px] text-gray-500 uppercase flex items-center gap-0.5">{f.l} <InfoTip text={f.tip} /></p>
                        {f.isLink ? <a href={`https://www.alldatasheet.com/view.jsp?Searchword=${encodeURIComponent(selectedComponent.mpn)}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline font-medium" data-testid="datasheet-link">View</a>
                        : <p className={`font-medium text-xs ${f.green ? 'text-green-700' : 'text-gray-900'}`}>{f.v}</p>}
                      </div>
                    ))}
                  </div>

                  {detailLoading ? <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-green-600" /></div> : compDetail ? (
                    <Tabs defaultValue="detail-pricing" className="w-full">
                      <TabsList className="bg-gray-100 h-8 mb-3">
                        <TabsTrigger value="detail-pricing" className="text-[11px] h-6 gap-1"><DollarSign className="h-3 w-3" /> Pricing</TabsTrigger>
                        <TabsTrigger value="detail-risk" className="text-[11px] h-6 gap-1"><Shield className="h-3 w-3" /> Risk & Compliance</TabsTrigger>
                        <TabsTrigger value="detail-alternates" className="text-[11px] h-6 gap-1"><ArrowRightLeft className="h-3 w-3" /> Alternates</TabsTrigger>
                        <TabsTrigger value="detail-distributors" className="text-[11px] h-6 gap-1"><Globe className="h-3 w-3" /> Distributors</TabsTrigger>
                      </TabsList>
                      {/* Detail: Pricing */}
                      <TabsContent value="detail-pricing" className="mt-0">
                        <div className="grid grid-cols-3 gap-3">
                          {['all', 'domestic', 'global'].map(region => {
                            const rp = getPricesForRegion(selectedComponent, region);
                            const label = region === 'all' ? 'ALL (Global+Domestic)' : region === 'domestic' ? 'Domestic' : 'Global';
                            const bSav = calcSavings(selectedComponent.client_price, rp.best, selectedComponent.quantity);
                            const pSav = calcProbSavings(selectedComponent.client_price, rp.anchor, selectedComponent.quantity);
                            return (
                              <div key={region} className="p-3 rounded-lg border bg-gray-50">
                                <p className="text-xs font-semibold text-gray-700 mb-2 border-b pb-1">{label}</p>
                                <div className="grid grid-cols-2 gap-2 text-[11px]">
                                  <div><span className="text-gray-500">Median:</span> <span className="font-semibold">${rp.median.toFixed(4)}</span></div>
                                  <div><span className="text-gray-500">Best:</span> <span className="font-semibold text-green-700">${rp.best.toFixed(4)}</span></div>
                                  <div><span className="text-gray-500">Neg. Anchor:</span> <span className="font-semibold text-blue-700">${rp.anchor.toFixed(4)}</span></div>
                                  <div><span className="text-gray-500">Client:</span> <span className="font-semibold">${selectedComponent.client_price?.toFixed(4)}</span></div>
                                  <div><span className="text-gray-500">Best Sav.:</span> <span className="font-semibold text-green-700">{bSav > 0 ? `$${bSav.toLocaleString()}` : '--'}</span></div>
                                  <div><span className="text-gray-500">Prob. Sav.:</span> <span className="font-semibold text-blue-700">{pSav > 0 ? `$${pSav.toLocaleString()}` : '--'}</span></div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200 flex items-center gap-4 mt-3">
                          <Target className="h-5 w-5 text-green-600 flex-shrink-0" />
                          <div className="flex-1"><p className="text-xs font-semibold text-green-800">Negotiation Anchor <InfoTip text={getAnchorFormula()} /></p><p className="text-[10px] text-green-700">max(0.4 × Client + 0.6 × Best, 0.70 × Client) — realistic negotiation target</p></div>
                          <p className="text-xl font-bold text-green-700">${getAnchorPrice(selectedComponent)?.toFixed(4)}</p>
                        </div>
                      </TabsContent>
                      {/* Detail: Risk & Compliance */}
                      <TabsContent value="detail-risk" className="mt-0">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-2">Risk Assessment</p>
                            <div className="space-y-1">
                              {[
                                { type: 'Lifecycle Risk', level: selectedComponent.lifecycle_status === 'Active' ? 'Low' : selectedComponent.lifecycle_status === 'NRND' ? 'Medium' : 'High', tip: 'Based on product lifecycle status.' },
                                { type: 'Lifecycle Status', level: selectedComponent.lifecycle_status, badge: true, tip: 'Current manufacturing status.' },
                                { type: 'YTEOL', level: selectedComponent.lifecycle_status === 'Active' ? `${Math.floor(5 + selectedComponent.mpn.length % 6)} yrs` : selectedComponent.lifecycle_status === 'NRND' ? '2 yrs' : 'EOL', raw: true, tip: 'Years To End Of Life.' },
                                { type: 'Supply Chain Risk', level: selectedComponent.supply_risk || 'Low', tip: 'Based on stock levels and lead times.' },
                                { type: 'Single Source', level: (compDetail.fff_alternates?.length || 0) === 0 ? 'Yes' : 'No', raw: true, tip: 'No known alternates = high risk.' },
                                { type: 'Lead Time', level: `${14 + (selectedComponent.mpn.charCodeAt(0) % 40)} days`, raw: true, tip: 'Estimated delivery time.' },
                              ].map((r, i) => (
                                <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">
                                  <span className="text-gray-700 flex items-center gap-0.5">{r.type} <InfoTip text={r.tip} /></span>
                                  {r.raw ? <span className="font-semibold text-gray-800">{r.level}</span> : r.badge ? <Badge className={lcColor(r.level)}>{r.level}</Badge> : <Badge className={riskColor(r.level)}>{r.level}</Badge>}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-2">Compliance</p>
                            <div className="space-y-1">
                              {[
                                { type: 'Environmental', level: 'Low', tip: 'Overall environmental compliance.' },
                                { type: 'EU-RoHS', level: selectedComponent.mpn.charCodeAt(0) % 7 !== 0 ? 'Yes' : 'No', raw: true, tip: 'EU Restriction of Hazardous Substances (2011/65/EU).' },
                                { type: 'CN-RoHS', level: selectedComponent.mpn.charCodeAt(0) % 5 !== 0 ? 'Yes' : 'No', raw: true, tip: 'China RoHS - Administrative Measures on EEP.' },
                                { type: 'REACH', level: selectedComponent.mpn.charCodeAt(1) % 5 !== 0 ? 'Yes' : 'No', raw: true, tip: 'EU chemical safety regulation.' },
                                { type: 'PFAS', level: selectedComponent.mpn.charCodeAt(2) % 3 !== 0 ? 'Yes' : 'No', raw: true, tip: 'Per- and Polyfluoroalkyl Substances compliance.' },
                              ].map((r, i) => (
                                <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">
                                  <span className="text-gray-700 flex items-center gap-0.5">{r.type} <InfoTip text={r.tip} /></span>
                                  {r.raw ? <span className={`font-semibold ${r.level === 'Yes' ? 'text-green-700' : 'text-red-600'}`}>{r.level}</span> : <Badge className={riskColor(r.level)}>{r.level}</Badge>}
                                </div>
                              ))}
                            </div>
                            <p className="text-xs font-semibold text-gray-700 mt-3 mb-1">Qualifications</p>
                            <div className="flex flex-wrap gap-1">{['AEC-Q100', 'AEC-Q200', 'JEDEC JESD47', 'IEC 60749', 'UL', 'IATF-16949'].filter((_, i) => (selectedComponent.mpn.charCodeAt(i) || 0) % 2 === 0).map(q => <Badge key={q} variant="outline" className="text-[9px]">{q}</Badge>)}</div>
                            <p className="text-xs font-semibold text-gray-700 mt-2 mb-1">Temperature</p>
                            <Badge variant="outline" className="text-[10px] mr-1">{['Commercial', 'Industrial', 'Automotive'][selectedComponent.mpn.length % 3]}</Badge>
                            <Badge variant="outline" className="text-[10px]">{['-40°C to +85°C', '-55°C to +125°C', '-40°C to +125°C'][selectedComponent.mpn.length % 3]}</Badge>
                          </div>
                        </div>
                      </TabsContent>
                      {/* Detail: Alternates */}
                      <TabsContent value="detail-alternates" className="mt-0">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-gray-700 flex items-center gap-1"><ArrowRightLeft className="h-3.5 w-3.5 text-purple-600" /> Alternates <Badge className="bg-green-100 text-green-800 text-[9px] ml-1">{compDetail.fff_alternates?.length || 0}</Badge></p>
                          <Select value={altTypeFilter} onValueChange={v => { setAltTypeFilter(v); setAltPage(0); }}><SelectTrigger className="w-28 h-6 text-[10px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Types</SelectItem><SelectItem value="FFF">FFF</SelectItem><SelectItem value="Pin-to-Pin">Pin-to-Pin</SelectItem><SelectItem value="Functional">Functional</SelectItem></SelectContent></Select>
                        </div>
                        {(() => {
                          const filtered = (compDetail.fff_alternates || []).filter(a => altTypeFilter === 'all' || (a.match_type || 'FFF') === altTypeFilter).sort((a, b) => a.price - b.price);
                          const paged = filtered.slice(altPage * ALT_PAGE_SIZE, (altPage + 1) * ALT_PAGE_SIZE);
                          const totalPages = Math.ceil(filtered.length / ALT_PAGE_SIZE);
                          return filtered.length === 0 ? <p className="text-xs text-gray-500 italic py-4 text-center">No alternates found.</p> : (
                            <><div className="overflow-x-auto border rounded-lg"><Table><TableHeader><TableRow className="bg-gray-50 text-xs">
                              <TableHead className="font-semibold"><span className="flex items-center gap-0.5">Alt MPN <InfoTip text="Alternate Manufacturer Part Number." /></span></TableHead>
                              <TableHead className="font-semibold"><span className="flex items-center gap-0.5">Manufacturer <InfoTip text="Alternate manufacturer." /></span></TableHead>
                              <TableHead className="font-semibold"><span className="flex items-center gap-0.5">Type <InfoTip text="FFF=Form-Fit-Function, Pin-to-Pin=exact pinout, Functional=same function." /></span></TableHead>
                              <TableHead className="font-semibold"><span className="flex items-center gap-0.5">Best Price <InfoTip text="Lowest price for this alternate." /></span></TableHead>
                              <TableHead className="font-semibold"><span className="flex items-center gap-0.5">Neg. Anchor <InfoTip text="Negotiation anchor price for alternate." /></span></TableHead>
                              <TableHead className="font-semibold">Savings</TableHead>
                              <TableHead className="font-semibold">Stock</TableHead>
                            </TableRow></TableHeader><TableBody>
                              {paged.map((alt, idx) => {
                                const altAnchor = alt.price ? +(alt.price * 1.12).toFixed(4) : null;
                                const savPct = alt.price < selectedComponent.client_price ? ((1 - alt.price / selectedComponent.client_price) * 100).toFixed(0) : null;
                                return (<TableRow key={idx} className="text-xs hover:bg-gray-50">
                                  <TableCell className="font-mono font-medium">{alt.mpn}</TableCell>
                                  <TableCell className="text-gray-600">{alt.manufacturer}</TableCell>
                                  <TableCell><Badge variant="outline" className="text-[9px]">{alt.match_type || 'FFF'}</Badge></TableCell>
                                  <TableCell className="text-green-700 font-semibold">${alt.price?.toFixed(4)}</TableCell>
                                  <TableCell className="text-blue-700">{altAnchor ? `$${altAnchor.toFixed(4)}` : '--'}</TableCell>
                                  <TableCell>{savPct ? <Badge className="bg-green-100 text-green-800 text-[9px]">-{savPct}%</Badge> : <span className="text-gray-400">--</span>}</TableCell>
                                  <TableCell><Badge variant="outline" className="text-[9px]">{alt.stock > 0 ? alt.stock.toLocaleString() : '--'}</Badge></TableCell>
                                </TableRow>);
                              })}
                            </TableBody></Table></div>
                            {totalPages > 1 && <div className="flex items-center justify-center gap-2 mt-2"><Button size="sm" variant="outline" className="h-6 text-[10px]" disabled={altPage === 0} onClick={() => setAltPage(p => p - 1)}>Prev</Button><span className="text-[10px] text-gray-500">{altPage + 1}/{totalPages}</span><Button size="sm" variant="outline" className="h-6 text-[10px]" disabled={altPage >= totalPages - 1} onClick={() => setAltPage(p => p + 1)}>Next</Button></div>}
                            </>);
                        })()}
                      </TabsContent>
                      {/* Detail: Distributors */}
                      <TabsContent value="detail-distributors" className="mt-0">
                        <div className="overflow-x-auto border rounded-lg"><Table><TableHeader><TableRow className="bg-gray-50 text-xs">
                          <TableHead className="font-semibold"><span className="flex items-center gap-0.5">Distributor <InfoTip text="Masked for standard-tier accounts." /></span></TableHead>
                          <TableHead className="font-semibold"><span className="flex items-center gap-0.5">Country <InfoTip text="Distributor's shipping country." /></span></TableHead>
                          <TableHead className="font-semibold">Price</TableHead>
                          <TableHead className="font-semibold"><span className="flex items-center gap-0.5">Type <InfoTip text="Authorized=manufacturer-approved. Independent=secondary market." /></span></TableHead>
                          <TableHead className="font-semibold"><span className="flex items-center gap-0.5">Rate <InfoTip text="Spot=current market. Negotiated=contract price." /></span></TableHead>
                          <TableHead className="font-semibold">vs Client</TableHead>
                        </TableRow></TableHeader><TableBody>
                          {(() => {
                            const cp = selectedComponent.client_price || 1; const bp = selectedComponent.best_price || 0.5;
                            return [
                              { name: 'Distributor A', country: 'USA', type: 'Authorized', rateType: 'Negotiated', price: cp * 0.98 },
                              { name: 'Distributor B', country: 'Germany', type: 'Authorized', rateType: 'Negotiated', price: cp * 0.94 },
                              { name: 'Distributor C', country: 'Japan', type: 'Authorized', rateType: 'Negotiated', price: cp * 0.91 },
                              { name: 'Distributor D', country: 'Taiwan', type: 'Independent', rateType: 'Spot', price: cp * 0.87 },
                              { name: 'Distributor E', country: 'India', type: 'Authorized', rateType: 'Negotiated', price: bp * 1.05 },
                              { name: 'Distributor F', country: 'South Korea', type: 'Independent', rateType: 'Spot', price: bp },
                            ].sort((a, b) => a.price - b.price).map((d, i) => {
                              const diff = ((d.price - cp) / cp * 100).toFixed(1);
                              return (<TableRow key={i} className="text-xs hover:bg-gray-50">
                                <TableCell className="font-medium text-gray-400 italic">{d.name}</TableCell>
                                <TableCell>{d.country}</TableCell>
                                <TableCell className="font-semibold">${d.price.toFixed(4)}</TableCell>
                                <TableCell><Badge className={d.type === 'Authorized' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'} variant="outline">{d.type}</Badge></TableCell>
                                <TableCell><Badge variant="outline" className="text-[9px]">{d.rateType}</Badge></TableCell>
                                <TableCell><Badge className={parseFloat(diff) < 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>{diff}%</Badge></TableCell>
                              </TableRow>);
                            });
                          })()}
                        </TableBody></Table></div>
                        <p className="text-[10px] text-gray-400 mt-1">Distributor names are masked — visible for premium accounts.</p>
                      </TabsContent>
                    </Tabs>
                  ) : null}
                    </div>
                  </>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>


          {/* ═══ Top Recommendations Tab ═══ */}
          <TabsContent value="recommendations" className="mt-4">
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="recommendations-tab-content">
              <CardHeader className="py-3 px-4 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Lightbulb className="h-4 w-4 text-amber-500" /> Top Recommendations
                    {recommendations?.run_version && <Badge variant="outline" className="text-[10px] h-4 bg-blue-50 text-blue-700">v{recommendations.run_version}</Badge>}
                    <InfoTip text="AI-generated high-impact actions covering cost savings and risk mitigation for the selected optimization version. Only significant opportunities are surfaced." />
                    {recommendations?.recommendations?.length > 0 && <Badge variant="outline" className="text-[10px] h-4">{recommendations.recommendations.length}</Badge>}
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={() => fetchRecommendations(true)} disabled={recoLoading} data-testid="regenerate-recommendations-btn">
                    <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${recoLoading ? 'animate-spin' : ''}`} /> {recoLoading ? 'Generating...' : 'Regenerate'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-3">
                {recoLoading && !recommendations ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3" data-testid="reco-loading">
                    <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
                    <p className="text-sm text-gray-500">Analyzing BOM and generating recommendations...</p>
                    <p className="text-xs text-gray-400">This may take a few seconds</p>
                  </div>
                ) : recoError && !recommendations ? (
                  <div className="text-center py-12">
                    <AlertTriangle className="h-10 w-10 text-red-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-600">{recoError}</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => fetchRecommendations(true)}>Try Again</Button>
                  </div>
                ) : recommendations ? (
                  <div className="space-y-4">
                    {/* Executive Summary */}
                    {recommendations.summary && (
                      <div className="p-4 bg-amber-50 rounded-lg border border-amber-200" data-testid="reco-summary">
                        <div className="flex items-start gap-3">
                          <Lightbulb className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1">Executive Summary</p>
                            <p className="text-sm text-amber-900 leading-relaxed">{recommendations.summary}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Recommendations List */}
                    <div className="space-y-3" data-testid="reco-list">
                      {(recommendations.recommendations || []).map((rec, idx) => {
                        const priorityStyles = {
                          critical: { border: 'border-l-red-600', bg: 'bg-red-50', badge: 'bg-red-100 text-red-800', icon: 'text-red-600' },
                          high: { border: 'border-l-orange-500', bg: 'bg-orange-50', badge: 'bg-orange-100 text-orange-800', icon: 'text-orange-600' },
                          medium: { border: 'border-l-blue-500', bg: 'bg-blue-50', badge: 'bg-blue-100 text-blue-800', icon: 'text-blue-600' },
                        };
                        const categoryIcons = {
                          savings: <DollarSign className="h-5 w-5" />,
                          risk: <AlertTriangle className="h-5 w-5" />,
                        };
                        const actionLabels = {
                          switch_alternate: { label: 'View Alternates', icon: <ArrowRightLeft className="h-3.5 w-3.5" />, action: () => setActiveTab('smart-alternates') },
                          change_distributor: { label: 'View Distributors', icon: <Globe className="h-3.5 w-3.5" />, action: () => setActiveTab('distributors') },
                          renegotiate: { label: 'Refer to 1Source', icon: <Handshake className="h-3.5 w-3.5" />, action: () => toast.success('Referred to 1Source for renegotiation') },
                          address_lifecycle: { label: 'View Risk Details', icon: <Shield className="h-3.5 w-3.5" />, action: () => setActiveTab('risk-compliance') },
                          mitigate_supply_risk: { label: 'View Risk Details', icon: <Shield className="h-3.5 w-3.5" />, action: () => setActiveTab('risk-compliance') },
                          qualify_alternate: { label: 'Find Alternates', icon: <ArrowRightLeft className="h-3.5 w-3.5" />, action: () => setActiveTab('smart-alternates') },
                          strategic_review: { label: 'View Components', icon: <Package className="h-3.5 w-3.5" />, action: () => setActiveTab('components') },
                        };
                        const ps = priorityStyles[rec.priority] || priorityStyles.medium;
                        const actionDef = actionLabels[rec.action_type] || actionLabels.strategic_review;
                        return (
                          <div key={rec.id} className={`border-l-4 ${ps.border} ${ps.bg} rounded-lg p-4 transition-all hover:shadow-sm`} data-testid={`reco-item-${rec.id}`}>
                            <div className="flex items-start gap-3">
                              <div className={`flex-shrink-0 mt-0.5 ${ps.icon}`}>
                                {categoryIcons[rec.category] || categoryIcons.savings}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                  <Badge className={`${ps.badge} text-[10px] h-5`}>{rec.priority?.toUpperCase()}</Badge>
                                  <Badge variant="outline" className="text-[10px] h-5 capitalize">{rec.category}</Badge>
                                  {rec.risk_type && <Badge variant="outline" className="text-[10px] h-5 capitalize">{rec.risk_type?.replace(/_/g, ' ')}</Badge>}
                                  {rec.estimated_savings > 0 && <Badge className="bg-green-100 text-green-800 text-[10px] h-5">${rec.estimated_savings?.toLocaleString()} savings</Badge>}
                                </div>
                                <p className="font-semibold text-sm text-gray-900 mb-1">{rec.title}</p>
                                <p className="text-xs text-gray-700 leading-relaxed">{rec.description}</p>
                                {rec.affected_components?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {rec.affected_components.slice(0, 6).map(mpn => (
                                      <Badge key={mpn} variant="outline" className="text-[9px] h-4 font-mono cursor-pointer hover:bg-gray-200"
                                        onClick={() => { setActiveTab('components'); setFilters(prev => ({ ...prev, search: mpn })); }}
                                        data-testid={`reco-mpn-${mpn}`}>{mpn}</Badge>
                                    ))}
                                    {rec.affected_components.length > 6 && <Badge variant="outline" className="text-[9px] h-4">+{rec.affected_components.length - 6} more</Badge>}
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col gap-1.5 flex-shrink-0">
                                <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white gap-1" onClick={actionDef.action} data-testid={`reco-action-${rec.id}`}>
                                  {actionDef.icon} {actionDef.label}
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                                  onClick={() => { setAssignOpen(true); setAssignCtx({ mpn: rec.affected_components?.[0], recommendation: rec.title }); }}
                                  data-testid={`reco-assign-${rec.id}`}>
                                  <UserPlus className="h-3.5 w-3.5" /> Assign
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Generation info */}
                    {recommendations.generated_at && (
                      <p className="text-[10px] text-gray-400 text-right mt-2" data-testid="reco-timestamp">
                        {recommendations.run_version ? `Optimization v${recommendations.run_version} | ` : ''}Generated: {new Date(recommendations.generated_at).toLocaleString()} | {recommendations.component_count} components analyzed
                      </p>
                    )}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---- Optimization Runs Tab ---- */}
          <TabsContent value="optimization" className="mt-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    <History className="h-5 w-5 text-green-600" /> Optimization Versions
                    <InfoTip text="Each optimization run creates an immutable snapshot of parameters, pricing, and risk outputs." />
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={fetchOptRuns} disabled={optRunsLoading}>
                    <RefreshCw className={`h-3.5 w-3.5 mr-1 ${optRunsLoading ? 'animate-spin' : ''}`} /> Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {optRunsLoading && optRuns.length === 0 ? (
                  <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-green-600" /></div>
                ) : optRuns.length === 0 ? (
                  <div className="text-center py-12">
                    <Sparkles className="h-12 w-12 text-gray-200 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No optimization runs yet.</p>
                    <p className="text-xs text-gray-400 mt-1">Go to BOM Intelligence to start an optimization.</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/bom')}>Back to Hub</Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {optRuns.map(run => {
                      const isExpanded = expandedRun === run.run_id;
                      const params = run.parameters || {};
                      return (
                        <div key={run.run_id} className="border rounded-lg overflow-hidden" data-testid={`opt-run-${run.run_id}`}>
                          {/* Run Summary Row */}
                          <button
                            className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${isExpanded ? 'bg-green-50 border-b' : 'hover:bg-gray-50'}`}
                            onClick={() => loadRunDetail(run.run_id)}
                            data-testid={`opt-run-toggle-${run.run_id}`}
                          >
                            {isExpanded ? <ChevronDown className="h-4 w-4 text-green-600 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                            <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                              <span className="font-mono font-bold text-sm">v{run.version}</span>
                              <Badge className={run.status === 'completed' ? 'bg-green-100 text-green-800' : run.status === 'running' ? 'bg-indigo-100 text-indigo-800' : 'bg-red-100 text-red-800'}>
                                {run.status === 'running' && <Loader2 className="h-3 w-3 animate-spin mr-1" />}{run.status}
                              </Badge>
                              <span className="text-xs text-gray-500">{run.created_at ? new Date(run.created_at).toLocaleString() : ''}</span>
                              <span className="text-gray-300">|</span>
                              <span className="text-xs text-gray-500">{params.target_incoterm}</span>
                              <span className="text-xs text-gray-500">{params.distributor_type}</span>
                              {params.include_alternates && <Badge variant="outline" className="text-[9px] h-4">+Alternates</Badge>}
                              {params.exclude_countries?.length > 0 && <Badge variant="outline" className="text-[9px] h-4">-{params.exclude_countries.length} countries</Badge>}
                            </div>
                            {run.total_savings != null && (
                              <div className="text-right flex-shrink-0">
                                <p className="text-xs text-gray-500">Savings</p>
                                <p className="text-sm font-bold text-green-700">${run.total_savings?.toLocaleString()}</p>
                              </div>
                            )}
                            {run.savings_pct != null && (
                              <Badge className="bg-green-100 text-green-800 flex-shrink-0">{run.savings_pct}%</Badge>
                            )}
                          </button>

                          {/* Expanded: Line Items */}
                          {isExpanded && (
                            <div className="p-3 bg-gray-50">
                              {runDetailLoading ? (
                                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
                              ) : expandedRunData ? (
                                <div className="space-y-3">
                                  {/* Run KPIs */}
                                  <div className="grid grid-cols-4 gap-3">
                                    {[
                                      { l: 'Original Cost', v: `$${expandedRunData.total_original?.toLocaleString()}` },
                                      { l: 'Optimized Cost', v: `$${expandedRunData.total_optimized?.toLocaleString()}`, green: true },
                                      { l: 'Total Savings', v: `$${expandedRunData.total_savings?.toLocaleString()}`, green: true },
                                      { l: 'Line Items', v: expandedRunData.total_line_items },
                                    ].map((k, i) => (
                                      <div key={i} className="bg-white p-2.5 rounded-lg border text-center">
                                        <p className="text-[10px] text-gray-500 uppercase">{k.l}</p>
                                        <p className={`font-bold text-sm ${k.green ? 'text-green-700' : 'text-gray-900'}`}>{k.v}</p>
                                      </div>
                                    ))}
                                  </div>

                                  {/* Risk Summary */}
                                  {expandedRunData.risk_summary && (
                                    <div className="flex gap-2">
                                      <Badge className="bg-red-100 text-red-800">{expandedRunData.risk_summary.high} High Risk</Badge>
                                      <Badge className="bg-yellow-100 text-yellow-800">{expandedRunData.risk_summary.medium} Medium</Badge>
                                      <Badge className="bg-green-100 text-green-800">{expandedRunData.risk_summary.low} Low</Badge>
                                    </div>
                                  )}

                                  {/* Parameters */}
                                  <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500">
                                    <span className="font-medium text-gray-700">Params:</span>
                                    <span>Incoterm: {params.target_incoterm}</span>
                                    <span className="text-gray-300">|</span>
                                    <span>Distributor: {params.distributor_type}</span>
                                    <span className="text-gray-300">|</span>
                                    <span>Alternates: {params.include_alternates ? 'Yes' : 'No'}</span>
                                    <span className="text-gray-300">|</span>
                                    <span>Shipment: {params.shipment_mode}</span>
                                    {params.exclude_countries?.length > 0 && <>
                                      <span className="text-gray-300">|</span>
                                      <span>Excluded: {params.exclude_countries.join(', ')}</span>
                                    </>}
                                  </div>

                                  {/* Line Items Table */}
                                  <div className="overflow-x-auto bg-white rounded-lg border">
                                    <Table>
                                      <TableHeader>
                                        <TableRow className="bg-gray-50 text-xs">
                                          <TableHead className="font-semibold">MPN</TableHead>
                                          <TableHead className="font-semibold">Manufacturer</TableHead>
                                          <TableHead className="font-semibold">Category</TableHead>
                                          <TableHead className="font-semibold">Qty</TableHead>
                                          <TableHead className="font-semibold">Original</TableHead>
                                          <TableHead className="font-semibold">Optimized</TableHead>
                                          <TableHead className="font-semibold">Line Savings</TableHead>
                                          <TableHead className="font-semibold">Risk</TableHead>
                                          <TableHead className="font-semibold">Alternate</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {(expandedRunData.line_items || []).map((item, idx) => (
                                          <TableRow
                                            key={idx}
                                            className="hover:bg-green-50/50 cursor-pointer transition-colors"
                                            onClick={() => navigate(`/component-intelligence/${encodeURIComponent(item.mpn)}`)}
                                            data-testid={`line-item-${idx}`}
                                          >
                                            <TableCell className="font-mono font-semibold text-xs text-green-800 flex items-center gap-1">
                                              {item.mpn}
                                              <ExternalLink className="h-3 w-3 text-gray-300" />
                                            </TableCell>
                                            <TableCell className="text-xs">{item.manufacturer}</TableCell>
                                            <TableCell><Badge variant="outline" className="text-[9px]">{item.category}</Badge></TableCell>
                                            <TableCell className="text-xs">{item.quantity?.toLocaleString()}</TableCell>
                                            <TableCell className="text-xs">${item.original_price?.toFixed(4)}</TableCell>
                                            <TableCell className="text-xs text-green-700 font-medium">${item.optimized_price?.toFixed(4)}</TableCell>
                                            <TableCell>
                                              {item.line_savings > 0 ? (
                                                <Badge className="bg-green-100 text-green-800 text-[10px]">${item.line_savings?.toLocaleString()}</Badge>
                                              ) : (
                                                <span className="text-xs text-gray-400">-</span>
                                              )}
                                            </TableCell>
                                            <TableCell><Badge className={riskColor(item.supply_risk || 'Low')}>{item.supply_risk || 'Low'}</Badge></TableCell>
                                            <TableCell className="text-xs">
                                              {item.alternate_mpn ? (
                                                <span className="text-blue-700 font-mono">{item.alternate_mpn}</span>
                                              ) : (
                                                <span className="text-gray-400">-</span>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                  <p className="text-[10px] text-gray-400 text-center">Click any line item to view Part Intelligence details</p>
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---- Risk & Compliance Tab ---- */}
          <TabsContent value="risk-compliance" className="mt-4">
            <div className="space-y-4">
              {/* Section A: Risk Assessment */}
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="py-3 px-4 border-b">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" /> Risk Assessment
                    <InfoTip text="Risk levels across lifecycle, supply chain, single-source, lead time and YTEOL." />
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-2">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50 text-xs">
                          <TableHead className="font-semibold">MPN</TableHead>
                          <TableHead className="font-semibold">Lifecycle Risk</TableHead>
                          <TableHead className="font-semibold">Lifecycle Status</TableHead>
                          <TableHead className="font-semibold"><span className="flex items-center gap-0.5">YTEOL <InfoTip text="Years to End of Life" /></span></TableHead>
                          <TableHead className="font-semibold">Supply Chain Risk</TableHead>
                          <TableHead className="font-semibold">Single Source</TableHead>
                          <TableHead className="font-semibold">Lead Time</TableHead>
                          <TableHead className="font-semibold">Max Risk</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {[...filteredComps].sort((a, b) => {
                          const riskW = { High: 3, Medium: 2, Low: 1 };
                          return (riskW[getMaxRisk(b)] || 0) - (riskW[getMaxRisk(a)] || 0);
                        }).map((c, idx) => {
                          const lcRisk = c.lifecycle_status === 'Active' ? 'Low' : c.lifecycle_status === 'NRND' ? 'Medium' : 'High';
                          const ssRisk = (c.alternates_count || 0) === 0;
                          const yteol = c.lifecycle_status === 'Active' ? Math.floor(Math.random() * 8) + 5 : c.lifecycle_status === 'NRND' ? Math.floor(Math.random() * 3) + 1 : 0;
                          const leadTime = Math.floor(Math.random() * 60) + 7;
                          return (
                            <TableRow key={idx} className="hover:bg-gray-50 text-xs" data-testid={`risk-row-${idx}`}>
                              <TableCell className="font-mono font-medium">{c.mpn}</TableCell>
                              <TableCell><Badge className={riskColor(lcRisk)}>{lcRisk}</Badge></TableCell>
                              <TableCell><Badge className={lcColor(c.lifecycle_status)}>{c.lifecycle_status}</Badge></TableCell>
                              <TableCell className="font-semibold">{yteol > 0 ? `${yteol} yrs` : 'EOL'}</TableCell>
                              <TableCell><Badge className={riskColor(c.supply_risk || 'Low')}>{c.supply_risk || 'Low'}</Badge></TableCell>
                              <TableCell>{ssRisk ? <Badge className="bg-red-100 text-red-800 text-[9px]">Yes</Badge> : <Badge className="bg-green-100 text-green-800 text-[9px]">No</Badge>}</TableCell>
                              <TableCell>{leadTime} days</TableCell>
                              <TableCell><Badge className={`${riskColor(getMaxRisk(c))} font-bold`}>{getMaxRisk(c)}</Badge></TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Section B: Compliance */}
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="py-3 px-4 border-b">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-500" /> Compliance
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-2">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50 text-xs">
                          <TableHead className="font-semibold">MPN</TableHead>
                          <TableHead className="font-semibold">Environmental</TableHead>
                          <TableHead className="font-semibold">EU-RoHS</TableHead>
                          <TableHead className="font-semibold">CN-RoHS</TableHead>
                          <TableHead className="font-semibold">REACH</TableHead>
                          <TableHead className="font-semibold">PFAS</TableHead>
                          <TableHead className="font-semibold">Qualifications</TableHead>
                          <TableHead className="font-semibold">Temp Grade</TableHead>
                          <TableHead className="font-semibold">Temp Range</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredComps.map((c, idx) => {
                          const euRohs = Math.random() > 0.15;
                          const cnRohs = Math.random() > 0.12;
                          const reach = Math.random() > 0.2;
                          const pfas = Math.random() > 0.6;
                          const envRisk = euRohs && cnRohs && reach ? 'Low' : euRohs || reach ? 'Medium' : 'High';
                          const quals = ['AEC-Q100', 'AEC-Q200', 'JEDEC JESD47', 'IEC 60749', 'UL', 'IATF-16949', 'IEC 60384-14', 'IEC 60115-8'].filter(() => Math.random() > 0.7);
                          const tempGrades = ['Commercial', 'Industrial', 'Automotive', 'Military', 'Space', 'Medical'];
                          const tempGrade = tempGrades[Math.floor(Math.random() * 4)];
                          const tempRanges = ['-40°C to +85°C', '-55°C to +125°C', '-40°C to +125°C', '0°C to +70°C', '-55°C to +155°C'];
                          const tempRange = tempRanges[Math.floor(Math.random() * tempRanges.length)];
                          return (
                            <TableRow key={idx} className="hover:bg-gray-50 text-xs">
                              <TableCell className="font-mono font-medium">{c.mpn}</TableCell>
                              <TableCell><Badge className={riskColor(envRisk)}>{envRisk}</Badge></TableCell>
                              <TableCell>{euRohs ? <span className="text-green-700 font-semibold">Yes</span> : <span className="text-red-600">No</span>}</TableCell>
                              <TableCell>{cnRohs ? <span className="text-green-700 font-semibold">Yes</span> : <span className="text-red-600">No</span>}</TableCell>
                              <TableCell>{reach ? <span className="text-green-700 font-semibold">Yes</span> : <span className="text-red-600">No</span>}</TableCell>
                              <TableCell>{pfas ? <span className="text-green-700 font-semibold">Yes</span> : <span className="text-red-600">No</span>}</TableCell>
                              <TableCell><div className="flex flex-wrap gap-0.5">{quals.length > 0 ? quals.map(q => <Badge key={q} variant="outline" className="text-[9px] h-4">{q}</Badge>) : <span className="text-gray-400">--</span>}</div></TableCell>
                              <TableCell><Badge variant="outline" className="text-[10px]">{tempGrade}</Badge></TableCell>
                              <TableCell><Badge variant="outline" className="text-[10px]">{tempRange}</Badge></TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ---- Smart Alternates Tab ---- */}
          <TabsContent value="smart-alternates" className="mt-4">
            <BOMSmartAlternates bomId={bomId} bomName={bom?.name} />
          </TabsContent>

          {/* ---- Distributor Intelligence Tab ---- */}
          <TabsContent value="distributors" className="mt-4">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="py-3 px-4 border-b">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Globe className="h-4 w-4 text-blue-600" /> Distributor Intelligence
                  <InfoTip text="Per-component distributor pricing. Distributor names are masked for standard-tier accounts." />
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                {filteredComps.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-12">No components to show.</p>
                ) : (
                  <div className="space-y-4">
                    {filteredComps.slice(0, 20).map((comp, cIdx) => {
                      const clientPrice = comp.client_price || 1;
                      const basePrice = comp.best_price || 0.5;
                      const distributors = [
                        { name: 'Distributor A', country: 'USA', type: 'Authorized', rateType: 'Negotiated', price: clientPrice * 0.98 },
                        { name: 'Distributor B', country: 'Germany', type: 'Authorized', rateType: 'Negotiated', price: clientPrice * 0.94 },
                        { name: 'Distributor C', country: 'Japan', type: 'Authorized', rateType: 'Negotiated', price: clientPrice * 0.91 },
                        { name: 'Distributor D', country: 'Taiwan', type: 'Independent', rateType: 'Spot', price: clientPrice * 0.87 },
                        { name: 'Distributor E', country: 'India', type: 'Authorized', rateType: 'Negotiated', price: basePrice * 1.05 },
                        { name: 'Distributor F', country: 'South Korea', type: 'Independent', rateType: 'Spot', price: basePrice },
                      ].sort((a, b) => a.price - b.price);
                      return (
                        <div key={cIdx} className="border rounded-lg overflow-hidden" data-testid={`dist-comp-${cIdx}`}>
                          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b">
                            <span className="font-mono font-semibold text-xs text-gray-900">{comp.mpn}</span>
                            <CPNBadge mpn={comp.mpn} />
                            <Badge variant="outline" className="text-[9px]">{comp.category}</Badge>
                            <span className="ml-auto text-[10px] text-gray-500">Client: ${clientPrice.toFixed(4)}</span>
                          </div>
                          <Table>
                            <TableHeader>
                              <TableRow className="text-[10px]">
                                <TableHead className="font-semibold py-1.5">Distributor</TableHead>
                                <TableHead className="font-semibold py-1.5">Country of Supply</TableHead>
                                <TableHead className="font-semibold py-1.5">Price</TableHead>
                                <TableHead className="font-semibold py-1.5">Distributor Type</TableHead>
                                <TableHead className="font-semibold py-1.5">Rate Type</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {distributors.map((d, i) => (
                                <TableRow key={i} className="text-xs">
                                  <TableCell className="font-medium text-gray-400 italic py-1.5">{d.name}</TableCell>
                                  <TableCell className="py-1.5">{d.country}</TableCell>
                                  <TableCell className="font-semibold py-1.5">${d.price.toFixed(4)}</TableCell>
                                  <TableCell className="py-1.5">
                                    <Badge className={d.type === 'Authorized' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'} variant="outline">{d.type}</Badge>
                                  </TableCell>
                                  <TableCell className="py-1.5">
                                    <Badge variant="outline" className="text-[9px]">{d.rateType}</Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      );
                    })}
                    {filteredComps.length > 20 && (
                      <p className="text-xs text-gray-400 text-center">Showing top 20 components. Use search to find specific parts.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* ---- Add Component Dialog ---- */}
        <Dialog open={addCompOpen} onOpenChange={setAddCompOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Component to BOM</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">MPN *</label>
                <Input value={newComp.mpn} onChange={e => setNewComp({...newComp, mpn: e.target.value})} placeholder="e.g., STM32F407VGT6" data-testid="new-comp-mpn" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Description</label>
                <Input value={newComp.description} onChange={e => setNewComp({...newComp, description: e.target.value})} placeholder="Component description" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Quantity</label>
                  <Input type="number" value={newComp.quantity} onChange={e => setNewComp({...newComp, quantity: parseInt(e.target.value) || 1})} />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Category</label>
                  <Input value={newComp.category} onChange={e => setNewComp({...newComp, category: e.target.value})} placeholder="e.g., MCU" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Manufacturer</label>
                <Input value={newComp.manufacturer} onChange={e => setNewComp({...newComp, manufacturer: e.target.value})} placeholder="e.g., STMicroelectronics" />
              </div>
              <Button onClick={handleAddComponent} className="w-full bg-green-600 hover:bg-green-700 text-white" disabled={!newComp.mpn} data-testid="confirm-add-component">
                Add Component
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ---- Bulk Send to 1Source ---- */}
        {components.filter(c => c.supply_risk === 'High').length > 0 && (
          <Card className="bg-red-50 border-red-200 shadow-sm mt-4">
            <CardContent className="pt-4 pb-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-red-800">
                  {components.filter(c => c.supply_risk === 'High').length} high-risk components detected
                </p>
                <p className="text-xs text-red-600">Send all high-risk parts for sourcing to find alternatives</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => toast.success(`${components.filter(c => c.supply_risk === 'High').length} high-risk parts sent to 1Source`)} data-testid="bulk-send-1source">
                  Send to 1Source
                </Button>
                <Button size="sm" variant="outline" className="border-red-300 text-red-700" onClick={() => toast.success('Sent to 1Spot for urgent sourcing')} data-testid="bulk-send-1spot">
                  Send to 1Spot
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5 text-purple-600" /> Side-by-Side Comparison
              </DialogTitle>
            </DialogHeader>
            {selectedComponent && compareAlt && (
              <div className="grid grid-cols-2 gap-4" data-testid="datasheet-comparison">
                <div className="border border-gray-200 rounded-lg p-4">
                  <Badge className="bg-blue-100 text-blue-800 mb-2">Original</Badge>
                  <h4 className="font-mono font-bold text-gray-900 text-lg">{selectedComponent.mpn}</h4>
                  <p className="text-sm text-gray-500 mb-4">{selectedComponent.category}</p>
                  <div className="space-y-3">
                    {[
                      { l: 'Your Price', v: `$${selectedComponent.client_price?.toFixed(3)}` },
                      { l: 'Quantity', v: selectedComponent.quantity?.toLocaleString() },
                      { l: 'Supply Risk', v: selectedComponent.supply_risk },
                      { l: 'Lifecycle', v: selectedComponent.lifecycle_status },
                      { l: 'Supplier', v: selectedComponent.supplier_name },
                    ].map((f, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-gray-500">{f.l}</span>
                        <span className="font-medium text-gray-900">{f.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                  <Badge className="bg-green-100 text-green-800 mb-2">Alternate</Badge>
                  <h4 className="font-mono font-bold text-gray-900 text-lg">{compareAlt.mpn}</h4>
                  <p className="text-sm text-gray-500 mb-4">{compareAlt.manufacturer}</p>
                  <div className="space-y-3">
                    {[
                      { l: 'Price', v: `$${compareAlt.price?.toFixed(3)}` },
                      { l: 'Stock', v: compareAlt.stock?.toLocaleString() },
                      { l: 'Match Score', v: `${compareAlt.match_score}%` },
                      { l: 'Match Type', v: compareAlt.match_type },
                      { l: 'Lead Time', v: `${compareAlt.lead_time_days} days` },
                      { l: 'Package', v: compareAlt.package },
                    ].map((f, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-gray-500">{f.l}</span>
                        <span className="font-medium text-gray-900">{f.v}</span>
                      </div>
                    ))}
                  </div>
                  {compareAlt.specs && (
                    <div className="mt-4 pt-3 border-t border-green-200">
                      <p className="text-xs font-semibold text-gray-700 mb-2">Specifications</p>
                      {Object.entries(compareAlt.specs).map(([k, v]) => (
                        <div key={k} className="flex justify-between text-xs mb-1">
                          <span className="text-gray-500 capitalize">{k.replace(/_/g, ' ')}</span>
                          <span className="font-medium">{v}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="col-span-2 bg-white rounded-lg border border-gray-200 p-4">
                  <h5 className="font-semibold text-gray-900 mb-2">Savings Summary</h5>
                  <div className="flex items-center gap-6">
                    <div>
                      <p className="text-xs text-gray-500">Price Difference</p>
                      <p className={`text-xl font-bold ${compareAlt.price < selectedComponent.client_price ? 'text-green-600' : 'text-red-600'}`}>
                        {compareAlt.price < selectedComponent.client_price ? '-' : '+'}${Math.abs(selectedComponent.client_price - compareAlt.price).toFixed(3)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Per Unit Savings %</p>
                      <p className={`text-xl font-bold ${compareAlt.price < selectedComponent.client_price ? 'text-green-600' : 'text-red-600'}`}>
                        {((1 - compareAlt.price / selectedComponent.client_price) * 100).toFixed(1)}%
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Total Impact ({selectedComponent.quantity?.toLocaleString()} units)</p>
                      <p className={`text-xl font-bold ${compareAlt.price < selectedComponent.client_price ? 'text-green-600' : 'text-red-600'}`}>
                        ${Math.abs((selectedComponent.client_price - compareAlt.price) * selectedComponent.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        <QuickAssignModal open={assignOpen} onClose={() => setAssignOpen(false)} context={assignCtx} />

        {/* ══ Optimize Dialog ══ */}
        <Dialog open={showOptDialog} onOpenChange={setShowOptDialog}>
          <DialogContent className="max-w-2xl" data-testid="optimize-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <SlidersHorizontal className="h-4 w-4 text-green-600" />
                {bom?.status === 'optimized' ? 'Re-Optimize' : 'Optimize'} BOM
              </DialogTitle>
              <DialogDescription className="text-xs">
                Configure parameters for optimization of <strong>{bom?.name}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto pr-1">
              {/* Row 1: Pricing Data Recency & Distributor Type */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium">Pricing Data Recency</Label>
                  <Select value={optParams.data_recency} onValueChange={v => setOptParams(p => ({...p, data_recency: v}))}>
                    <SelectTrigger className="h-8 mt-1 text-sm" data-testid="opt-data-recency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['6months','12months','18months','24months','30months','36months'].map(v => (
                        <SelectItem key={v} value={v}>{v.replace('months',' Months')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium">Distributor Type</Label>
                  <Select value={optParams.distributor_type} onValueChange={v => setOptParams(p => ({...p, distributor_type: v}))}>
                    <SelectTrigger className="h-8 mt-1 text-sm" data-testid="opt-distributor-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="authorized">Authorized Only</SelectItem>
                      <SelectItem value="independent">Independent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Row 2: Target Incoterm & Shipment Mode */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium">Target Incoterm</Label>
                  <Select value={optParams.target_incoterm} onValueChange={v => setOptParams(p => ({...p, target_incoterm: v, shipment_mode: v === 'EXW' ? 'air' : p.shipment_mode}))}>
                    <SelectTrigger className="h-8 mt-1 text-sm" data-testid="opt-incoterm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['EXW','CIF','CIF+DUTY','DDP','BOM Specific'].map(v => (
                        <SelectItem key={v} value={v}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className={optShipmentDisabled ? 'opacity-50' : ''}>
                  <Label className="text-xs font-medium flex items-center gap-1">
                    International Shipment Mode
                    {optShipmentDisabled && <span className="text-[10px] text-gray-400 font-normal">(N/A for EXW)</span>}
                  </Label>
                  <Select value={optParams.shipment_mode} onValueChange={v => setOptParams(p => ({...p, shipment_mode: v}))} disabled={optShipmentDisabled}>
                    <SelectTrigger className="h-8 mt-1 text-sm" data-testid="opt-shipment"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="air"><span className="flex items-center gap-1"><Plane className="h-3 w-3" /> Air</span></SelectItem>
                      <SelectItem value="sea"><span className="flex items-center gap-1"><Ship className="h-3 w-3" /> Sea</span></SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Exclude Country of Supply */}
              <div>
                <Label className="text-xs font-medium">Exclude Country of Supply</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5 p-2 border rounded-lg max-h-28 overflow-y-auto bg-gray-50">
                  {optCountries.map(c => (
                    <button key={c} onClick={() => toggleOptCountry(c)} className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${optParams.exclude_countries.includes(c) ? 'bg-red-100 text-red-800 border-red-300' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`} data-testid={`opt-country-${c}`}>
                      {c} {optParams.exclude_countries.includes(c) ? '\u2715' : ''}
                    </button>
                  ))}
                  {optCountries.length === 0 && <span className="text-xs text-gray-400">Loading countries...</span>}
                </div>
                <div className="flex gap-1.5 mt-1.5">
                  <Input placeholder="Add country manually..." className="h-7 text-xs flex-1" data-testid="manual-country-input"
                    onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { const v = e.target.value.trim(); if (!optCountries.includes(v)) setOptCountries(prev => [...prev, v]); toggleOptCountry(v); e.target.value = ''; } }} />
                </div>
                {optParams.exclude_countries.length > 0 && <p className="text-[10px] text-red-600 mt-1">{optParams.exclude_countries.length} excluded</p>}
              </div>
              {/* Toggles: Include Alternates, Spot Prices, Imports as Domestic */}
              <div className="grid grid-cols-3 gap-3">
                <div className="flex items-center gap-2">
                  <Switch checked={optParams.include_alternates} onCheckedChange={v => setOptParams(p => ({...p, include_alternates: v, alternates_exclude_countries: v ? p.alternates_exclude_countries : []}))} data-testid="opt-include-alts" />
                  <Label className="text-xs">Include Alternates</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={optParams.include_spot_prices} onCheckedChange={v => setOptParams(p => ({...p, include_spot_prices: v}))} data-testid="opt-include-spot" />
                  <Label className="text-xs">Include Spot Prices</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={optParams.imports_as_domestic} onCheckedChange={v => setOptParams(p => ({...p, imports_as_domestic: v}))} data-testid="opt-imports-domestic" />
                  <Label className="text-xs">Imports as Domestic</Label>
                </div>
              </div>
              {/* Alternates: Exclude Country of Origin */}
              {optParams.include_alternates && (
                <div>
                  <Label className="text-xs font-medium">Alternates: Exclude Country of Origin</Label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5 p-2 border rounded-lg max-h-28 overflow-y-auto bg-gray-50">
                    {optOriginCountries.map(c => (
                      <button key={`alt-${c}`} onClick={() => toggleOptAltCountry(c)} className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${optParams.alternates_exclude_countries.includes(c) ? 'bg-red-100 text-red-800 border-red-300' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`} data-testid={`opt-alt-country-${c}`}>
                        {c} {optParams.alternates_exclude_countries.includes(c) ? '\u2715' : ''}
                      </button>
                    ))}
                    {optOriginCountries.length === 0 && <span className="text-xs text-gray-400">Loading countries...</span>}
                  </div>
                  <div className="flex gap-1.5 mt-1.5">
                    <Input placeholder="Add country manually..." className="h-7 text-xs flex-1" data-testid="manual-alt-country-input"
                      onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { const v = e.target.value.trim(); if (!optOriginCountries.includes(v)) setOptOriginCountries(prev => [...prev, v]); toggleOptAltCountry(v); e.target.value = ''; } }} />
                  </div>
                  {optParams.alternates_exclude_countries.length > 0 && <p className="text-[10px] text-red-600 mt-1">{optParams.alternates_exclude_countries.length} excluded</p>}
                </div>
              )}
              {optParams.include_alternates && (<>
              {/* Compliances */}
              <div>
                <Label className="text-xs font-medium">Compliances</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {['EU-RoHS','CN-RoHS','REACH','PFAS'].map(v => (
                    <button key={v} onClick={() => toggleOptMulti('compliances', v)} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${optParams.compliances.includes(v) ? 'bg-green-100 text-green-800 border-green-300' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`} data-testid={`opt-compliance-${v}`}>
                      {v} {optParams.compliances.includes(v) ? '\u2713' : ''}
                    </button>
                  ))}
                </div>
              </div>
              {/* Temperature Grade */}
              <div>
                <Label className="text-xs font-medium">Temperature Grade</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {['Commercial','Commercial Extended','Industrial','Automotive','Military','High Temperature','Space','Medical','Telecom','Other'].map(v => (
                    <button key={v} onClick={() => toggleOptMulti('temperature_grade', v)} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${optParams.temperature_grade.includes(v) ? 'bg-blue-100 text-blue-800 border-blue-300' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`} data-testid={`opt-temp-${v}`}>
                      {v} {optParams.temperature_grade.includes(v) ? '\u2713' : ''}
                    </button>
                  ))}
                </div>
              </div>
              {/* Qualifications */}
              <div>
                <Label className="text-xs font-medium">Qualifications</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {['AEC','AUTO-QMS','AUTO-SAFETY','EMC','IEC-COMP','JEDEC','SAFETY-CERT','MIL','SPEC'].map(v => (
                    <button key={v} onClick={() => toggleOptMulti('qualifications', v)} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${optParams.qualifications.includes(v) ? 'bg-purple-100 text-purple-800 border-purple-300' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`} data-testid={`opt-qual-${v}`}>
                      {v} {optParams.qualifications.includes(v) ? '\u2713' : ''}
                    </button>
                  ))}
                </div>
              </div>
              </>)}
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setShowOptDialog(false)}>Cancel</Button>
              <Button size="sm" onClick={submitOptimizeWithParams} disabled={optDialogLoading} className="bg-green-600 hover:bg-green-700 text-white" data-testid="submit-optimize-btn">
                {optDialogLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
                {bom?.status === 'optimized' ? 'Re-Optimize' : 'Optimize'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default BOMDetailPage;
